import { GetContractResponseContent, EqCardKey, EqCardType, DurationType, IqModeSectionKey, ContractVariableType } from "@equaliq/api-model";
import DEMO_STOCK_PROFILE_PICTURE_ADAM from "src/assets/img/DemoStockProfilePictureAdam.jpg";
import DEMO_STOCK_PROFILE_PICTURE_ROGER from "src/assets/img/DemoStockProfilePictureRoger.jpg";
import DEFAULT_UNKNOWN_PROFILE_PICTURE from "src/assets/img/DefaultUnknownProfilePicture.png";

// ==================== DEMO CONTRACT IDS ====================
// Centralized contract ID constants for type safety
export const DEMO_CONTRACT_IDS = {
  ARTIST_RECORDING_DEAL: "demo_contract_artist_recording_001",
  PRODUCER_AGREEMENT: "demo_contract_producer_002",
  BRAND_PARTNERSHIP_NIL: "demo_contract_brand_partnership_003",
  SAFE_AGREEMENT: "demo_contract_safe_004",
} as const;

export type DemoContractId = typeof DEMO_CONTRACT_IDS[keyof typeof DEMO_CONTRACT_IDS];

// ==================== DEMO USER IDS ====================
export const DEMO_USER_IDS = {
  DEMO_USER_1: "demo_user_1",
  ADAM_SILVER: "demo_user_2",
  ROGER_GOODELL: "demo_user_3",
} as const;

export type DemoUserId = typeof DEMO_USER_IDS[keyof typeof DEMO_USER_IDS];

// ==================== DEMO USER DISPLAY INFO ====================
export const demoUserDisplayInfo: Record<DemoUserId, { displayName: string; email: string }> = {
  [DEMO_USER_IDS.DEMO_USER_1]: {
    displayName: "Demo User",
    email: "demo@example.com"
  },
  [DEMO_USER_IDS.ADAM_SILVER]: {
    displayName: "Adam Silver",
    email: "adam.silver@example.com"
  },
  [DEMO_USER_IDS.ROGER_GOODELL]: {
    displayName: "Roger Goodell",
    email: "roger.goodell@example.com"
  }
};

// ==================== DEMO PROFILE PICTURES ====================
export const demoProfilePictures: Record<DemoUserId, string> = {
  [DEMO_USER_IDS.DEMO_USER_1]: DEFAULT_UNKNOWN_PROFILE_PICTURE,
  [DEMO_USER_IDS.ADAM_SILVER]: DEMO_STOCK_PROFILE_PICTURE_ADAM,
  [DEMO_USER_IDS.ROGER_GOODELL]: DEMO_STOCK_PROFILE_PICTURE_ROGER
};

// ==================== DEMO CONTRACT SIGN DATA ====================
export const demoContractSignData = {
  fullName: "Mayowa Arogundade",
  defaultPenColor: "#FFFFFF",
  penColors: [
    { value: '#FFFFFF', label: 'White' },
    { value: '#6E53B8', label: 'Purple' },
    { value: '#547BB5', label: 'Blue' },
  ],
  quickSignConfirmation: "I confirm that the name entered above is my legal signature and I agree to sign this document.",
  signatureTerms: '<span class="text-yellow-400 font-semibold">&lt;DEMONSTRATION ONLY&gt;</span> I agree this electronic signature is legally binding and has the same effect as a handwritten signature.'
};

// ==================== DEMO CONTRACTS DATA ====================
const demoContractsArray: GetContractResponseContent[] = [
  // CONTRACT 1: Artist Recording Deal
  {
    contractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL,
    ownerId: DEMO_USER_IDS.DEMO_USER_1,
    name: "Artist Recording Deal",
    type: 'recording',

    // Contract Extraction - New structured approach replacing old flat properties
    contractExtraction: {
      extractedType: 'recording',
      parties: ["Artist", "Label"], 
      
      // Basic variables from recording agreement structure
      variables: {
        "total_advance": {
          name: "Total Advance",
          type: ContractVariableType.discovered_term,
          id: "total_advance",
          value: "$420,000 total advance payment",
          confidence: 1.0,
          firstOccurrence: 100,
          context: "Artist shall receive $420,000 as advance payment",
          variations: ["advance", "total advance"],
          definitionCitation: "Contract Provision - Advance"
        },
        "recoup_amount": {
          name: "Recoup Amount", 
          type: ContractVariableType.discovered_term,
          id: "recoup_amount",
          value: "$95,000 recording costs to be recouped",
          confidence: 0.95,
          firstOccurrence: 250,
          context: "Recording costs of $95,000 are recoupable",
          variations: ["recording recoup", "recoupable costs"],
          definitionCitation: "Contract Provision - Recording Costs"
        },
        "initial_term": {
          name: "Initial Term",
          type: ContractVariableType.discovered_term,
          id: "initial_term", 
          value: "2 years initial contract term",
          confidence: 1.0,
          firstOccurrence: 400,
          context: "Initial term of 2 years from execution",
          variations: ["term", "contract term"],
          definitionCitation: "Contract Provision - Initial Term"
        },
        "post_term_control": {
          name: "Post-Term Control",
          type: ContractVariableType.discovered_term,
          id: "post_term_control",
          value: "3 years post-term master control",
          confidence: 1.0,
          firstOccurrence: 450,
          context: "Label retains control for 3 years post-term",
          variations: ["post-term", "post term control"],
          definitionCitation: "Contract Provision - Post-Term Control"
        },
        "backend_royalty": {
          name: "Backend Royalty",
          type: ContractVariableType.discovered_term,
          id: "backend_royalty",
          value: "3% backend royalty rate",
          confidence: 1.0,
          firstOccurrence: 500,
          context: "Artist receives 3% backend royalty on gross revenue",
          variations: ["backend royalty", "royalty rate", "backend percentage"],
          definitionCitation: "Contract Provision - Backend Royalty"
        }
      },

      // Contract text from recording agreement
      contractTexts: {
        taggedText: {
          text: `RECORDING SERVICES AND MASTER RIGHTS AGREEMENT

Effective Date: January 15, 2024

Parties:
This Recording Services and Master Rights Agreement ("Agreement") is entered into by and between Midnight Records LLC, a Delaware limited liability company with offices at 123 Music Row, Nashville, Tennessee 37203 ("Label"), and Alexandra Rivers, an individual residing at 456 Sunset Boulevard, Los Angeles, California 90028 ("Artist").

Recitals:
A. Label desires to engage Artist to render exclusive recording services and to deliver commercial sound recordings.
B. Artist desires to render such services under the terms and conditions set forth below.
C. The parties intend this Agreement to define ownership rights, compensation terms, delivery obligations, and post-term rights.

1. Definitions
1.1 "<variable id="initial_term" type="discovered_term" level="1" instance="1">Initial Term</variable>" means the two (2) year period beginning on the Effective Date unless earlier terminated under this Agreement.
1.2 "Masters" means all master recordings, stems, and deliverable files created by or for Artist during the Term.
1.3 "Gross Receipts" means all amounts actually received by Label from exploitation of Masters, less taxes, refunds, chargebacks, and customary third-party fees.

2. Engagement and Delivery
2.1 Label hereby engages Artist, and Artist hereby accepts such engagement, to render exclusive recording services for the production and delivery of Masters.
2.2 Artist shall deliver no fewer than twelve (12) commercially satisfactory Masters during the <variable id="initial_term" type="discovered_term" level="1" instance="2">Initial Term</variable>, in accordance with Label's production schedules and quality standards.
2.3 Artist shall not record or deliver Masters for any third party during the Term without Label's prior written consent.
2.4 Failure to timely deliver required Masters may constitute a material breach and may suspend further advances until cured.

3. Advance Compensation
3.1 As consideration for Artist's services, Label shall pay Artist a <variable id="total_advance" type="discovered_term" level="1" instance="1">Total Advance</variable> of <variable id="total_advance" type="discovered_term" level="1" instance="2">$420,000</variable>, payable in installments described below.
3.2 Payment Schedule: (a) $145,000 on execution; (b) $80,000 upon delivery of the first EP; (c) $100,000 on commencement of the second EP; and (d) remaining amounts pursuant to written production milestones.
3.3 All payments are subject to applicable tax withholding requirements, and Artist is responsible for all personal income tax liabilities.

4. Recoupment and Accounting
4.1 Label may recoup <variable id="recoup_amount" type="discovered_term" level="1" instance="1">recording costs</variable> from royalties otherwise payable to Artist. Current recording costs are estimated at <variable id="recoup_amount" type="discovered_term" level="1" instance="2">$95,000</variable>.
4.2 The parties acknowledge that the recoupment threshold (the "<variable id="recoup_amount" type="discovered_term" level="1" instance="3">Recoup Amount</variable>") is expected to be approximately $95,000, subject to adjustment for actual approved expenses.
4.3 Label shall provide semi-annual royalty statements. Artist may audit such statements once per calendar year on reasonable prior notice.
4.4 No <variable id="backend_royalty" type="discovered_term" level="1" instance="1">Backend Royalty</variable> shall be payable until all recoupable amounts, including the Recoup Amount and any unrecouped <variable id="total_advance" type="discovered_term" level="1" instance="3">Total Advance</variable> balance, are fully recovered by Label.

5. Royalty Participation
5.1 After full recoupment, Label shall pay Artist a Backend Royalty equal to <variable id="backend_royalty" type="discovered_term" level="1" instance="2">3%</variable> of Gross Receipts derived from exploitation of Masters.
5.2 Royalty payments shall be made within forty-five (45) days following each accounting period and accompanied by supporting statements.
5.3 Label may maintain commercially reasonable reserves against expected returns and disputed receipts.

6. Term, Options, and Post-Term Rights
6.1 The Initial Term is two (2) years from the Effective Date.
6.2 Label shall have up to two (2) separate one-year option periods exercisable by written notice to Artist before expiration of the then-current term.
6.3 Following expiration or termination of the <variable id="initial_term" type="discovered_term" level="1" instance="3">initial contract period</variable>, Label shall retain <variable id="post_term_control" type="discovered_term" level="1" instance="1">Post-Term Control</variable> over delivered Masters for <variable id="post_term_control" type="discovered_term" level="1" instance="2">three years</variable>, solely for continued commercial exploitation and collection.
6.4 Upon conclusion of the Post-Term Control period, rights not expressly retained by Label shall revert to Artist, subject to outstanding third-party obligations.

7. Ownership and Intellectual Property
7.1 Except as expressly stated otherwise, all Masters and related deliverables created under this Agreement are works made for hire for Label.
7.2 To the extent any rights do not qualify as works made for hire, Artist irrevocably assigns all right, title, and interest in such rights to Label.
7.3 Artist retains ownership of Artist's name, likeness, and pre-existing intellectual property not incorporated into Masters.

8. Marketing and Promotion
8.1 Label shall use commercially reasonable efforts to market and distribute the Masters, including digital promotion, press, playlisting, and social campaigns.
8.2 Artist agrees to participate in reasonable promotional activities, interviews, and appearances scheduled in consultation with Artist's management.
8.3 Label shall have approval rights over release strategy, artwork, and final metadata formatting.

9. Representations, Warranties, and Covenants
9.1 Each party represents it has full authority to enter into and perform this Agreement.
9.2 Artist represents that deliveries shall be original or properly licensed, and that deliveries will not infringe third-party rights.
9.3 Label represents it shall account to Artist in good faith and according to industry-standard accounting practices.

10. Default and Termination
10.1 Either party may terminate this Agreement if the other party materially breaches and fails to cure within thirty (30) days after written notice.
10.2 Upon termination, Label shall retain ownership and exploitation rights in all Masters delivered and accepted prior to termination.
10.3 Termination does not affect accrued payment obligations, indemnities, confidentiality obligations, or other provisions intended to survive.

11. Miscellaneous
11.1 This Agreement is the entire agreement between the parties regarding the subject matter and supersedes all prior proposals, negotiations, and communications.
11.2 Any amendment must be in writing and signed by both parties.
11.3 This Agreement shall be governed by the laws of the State of California, without regard to conflict-of-law principles.
11.4 Venue for all disputes shall be state or federal courts located in Los Angeles County, California.

12. Signatures
By signing below, each party agrees to be bound by this Agreement as of the Effective Date.

MIDNIGHT RECORDS LLC
By: ______________________
Name: ____________________
Title: ____________________
Date: ____________________

ARTIST
By: ______________________
Name: Alexandra Rivers
Date: ____________________`
        }
      }
    },

    eqData: {
      cards: {
        [EqCardKey.moneyYouReceive]: {
          id: EqCardKey.moneyYouReceive,
          title: "Money You Receive",
          type: EqCardType.A,
          cardUniqueData: {
            MONEY_RECEIVED: {
              majorNumber: "$420K",
              paidAfterList: [
                "Signing bonus - $145K",
                "First EP delivery - $80K",
                "Start of Second EP - $100K",
                "Recording costs (recouped) - -$95K"
              ]
            }
          },
          ttsSrcUrl: "/audio/TTS Audio/Contract 1/Contract 1 EQ Terms/Contract 1 - EQ Card 1 - Money You_ll Receive.mp3",
          totalAdvance: "$420K",
          items: [
            {
              title: "Signing bonus",
              value: "$145K"
            },
            {
              title: "First EP delivery",
              value: "$80K"
            },
            {
              title: "Start of Second EP",
              value: "$100K"
            },
            {
              title: "Recording costs (recouped)",
              value: "-$95K"
            }
          ]
        },
        [EqCardKey.whatYouOwn]: {
          id: EqCardKey.whatYouOwn,
          title: "What You Own (and Don't)",
          type: EqCardType.A,
          cardUniqueData: {
            OWNERSHIP: {
              ownershipTerms: [
                {
                  title: "Ancillary commission",
                  description: "12.5%"
                },
                {
                  title: "Tour commission", 
                  description: "5%"
                }
              ]
            }
          },
          ttsSrcUrl: "/audio/TTS Audio/Contract 1/Contract 1 EQ Terms/Contract 1 - EQ Card 2 - What you Own.mp3",
          items: [
            {
              title: "Ancillary commission",
              value: "12.5%"
            },
            {
              title: "Tour commission",
              value: "5%"
            }
          ]
        },
        [EqCardKey.whatYoureResponsibleFor]: {
          id: EqCardKey.whatYoureResponsibleFor,
          title: "What You're Responsible For",
          type: EqCardType.A,
          cardUniqueData: {
            RESPONSIBILITIES: {
              responsibilities: [
                {
                  title: "Deliverables",
                  description: "2 EPs (5+6 tracks)"
                },
                {
                  title: "Sample Clearance",
                  description: "All samples & features cleared"
                },
                {
                  title: "Delivery Timeline",
                  description: "18 months total"
                }
              ]
            }
          },
          subTitle: "Deliverables",
          ttsSrcUrl: "/audio/TTS Audio/Contract 1/Contract 1 EQ Terms/Contract 1 - EQ Card 3 - What You_re Responsible For.mp3",
          items: [
            {
              title: "Deliverables",
              value: "2 EPs (5+6 tracks)"
            },
            {
              title: "Sample Clearance",
              value: "All samples & features cleared"
            },
            {
              title: "Delivery Timeline",
              value: "18 months total"
            }
          ]
        },
        [EqCardKey.howLongThisDealLasts]: {
          id: EqCardKey.howLongThisDealLasts,
          title: "How Long This Deal Lasts",
          type: EqCardType.B,
          cardUniqueData: {
            DURATION: {
              durationType: DurationType.fixed,
              durationText: "2 years initial term plus 3 years post-term control (~5 years total)",
              durationDetails: [
                {
                  title: "Initial Term",
                  description: "2 years"
                },
                {
                  title: "Post-Term Control",
                  description: "3 years"
                },
                {
                  title: "Total Lock-In",
                  description: "~5 years"
                }
              ]
            }
          },
          ttsSrcUrl: "/audio/TTS Audio/Contract 1/Contract 1 EQ Terms/Contract 1 - EQ Card 4 - How Long This Deal Lasts.mp3",
          items: [
            {
              title: "Initial Term",
              value: "2 years"
            },
            {
              title: "Post-Term Control",
              value: "3 years"
            },
            {
              title: "Total Lock-In",
              value: "~5 years"
            }
          ]
        },
        [EqCardKey.risksCostsLegalStuff]: {
          id: EqCardKey.risksCostsLegalStuff,
          title: "Risks, Costs & Legal Stuff",
          type: EqCardType.A,
          cardUniqueData: {
            LEGAL: {
              risks: "$95K recording recoup",
              costs: "No early termination",
              legal: "Breach = payback of all unrecouped funds"
            }
          },
          ttsSrcUrl: "/audio/TTS Audio/Contract 1/Contract 1 EQ Terms/Contract 1 - EQ Card 5 - Risks, Costs & Legal Stuff.mp3",
          items: [
            {
              title: "Risks",
              value: "$95K recording recoup"
            },
            {
              title: "Costs",
              value: "No early termination"
            },
            {
              title: "Liabilities",
              value: "Breach = payback of all unrecouped funds"
            }
          ]
        }
      }
    },
    iqData: {
    iqModeData: {
      // Artist Perspective
      "Artist": {
        sections: {
          // Section 1: Earnings
          [IqModeSectionKey.earnings]: {
            id: IqModeSectionKey.earnings,
            sectionTitle: "Earnings",
            questions: [
              {
                question: { text: "How will earnings be paid, and what needs to be paid back first?" },
                answer: { text: "You won't start getting paid royalties until the label fully recoups the $420K Total Advance and the $95,000 recording costs they've fronted on your behalf. That process is called Advance Recoupment, and it can delay your earnings even if your music is doing well. The more the label spends, the longer it takes before you see money." },
                ttsSrcUrl: "/audio/TTS Audio/Contract 1/Contract 1 EQ Terms/Contract 1 - EQ Card 1 - Money You_ll Receive.mp3"
              },
              {
                question: { text: "Are there other fees or deductions that can reduce payouts?" },
                answer: { text: "Yes — in addition to the advance, your royalties may be reduced by Deductible Expenses like promotional campaigns, featured guest fees, or third-party content licensing. These add up quickly and can eat into your Backend Royalty (3% of gross revenue) without much transparency if you're not watching closely." }
              },
              {
                question: { text: "What royalty percentages will I earn?" },
                answer: { text: "You're entitled to percentages set in the Royalty Rate Table: 18% of gross revenue from music sales, 15% on streams, and 12.5% on syncs. Additionally, you'll earn a 3% Backend Royalty on gross revenue after recoupment. These rates can go up if your project hits major milestones." }
              },
              {
                question: { text: "When do I actually receive the advance payments?" },
                answer: { text: "The $420K Total Advance is paid in installments: $145K at signing, $80K upon first EP delivery, $100K at the start of the second EP, and $95K covering recording costs. Your royalty payments only start after all advances and recording costs are fully recouped." }
              }
            ]
          },

          // Section 2: Quality of Rights
          [IqModeSectionKey.qualityOfRights]: {
            id: IqModeSectionKey.qualityOfRights,
            sectionTitle: "Quality of Rights",
            questions: [
              {
                question: { text: "Who owns the music created under this deal?" },
                answer: { text: "The label owns the Master Recordings you deliver. This means they control distribution and licensing, and you don't have legal ownership — even though it's your work. Owning your masters gives long-term control, so it's a big tradeoff." }
              },
              {
                question: { text: "Will the rights ever return to me?" },
                answer: { text: "There's a Reversion Clause that allows you to request your masters back three years after the contract ends, but it's not automatic — the label has to agree. This means you're unlikely to get full control unless you renegotiate later." }
              },
              {
                question: { text: "Can I control how the music is used or licensed?" },
                answer: { text: "Not really — the Usage Control Clause gives the label final say on where and how your music is used. You won't be able to block sync placements or delay releases if you disagree." }
              },
              {
                question: { text: "What happens to rights after the initial term ends?" },
                answer: { text: "Even after the 2-year initial term ends, the label maintains post-term control for an additional three years. This means you're looking at 5 years total before potentially regaining any rights through the reversion clause." }
              }
            ]
          },

          // Section 3: Usage Obligations
          [IqModeSectionKey.usageObligations]: {
            id: IqModeSectionKey.usageObligations,
            sectionTitle: "Usage & Obligations",
            questions: [
              {
                question: { text: "What am I expected to deliver under this contract?" },
                answer: { text: "You're required to deliver 2 fully mixed and mastered EPs, totaling at least 11 songs. The Delivery Obligation Clause ties payments and contract progression to meeting this exact requirement." }
              },
              {
                question: { text: "Can I release other music or collaborate with others during the term?" },
                answer: { text: "You can't drop solo projects, collab tapes, or even feature on someone else's song unless the label signs off. The Exclusivity Clause restricts what you can do creatively outside this deal for the entire contract term." }
              },
              {
                question: { text: "Are there expectations around promotional participation?" },
                answer: { text: "Yes — the Promotion Participation Clause means you're expected to post, do interviews, and show up to events when asked. Not doing so can lead to payment delays or trigger the Breach Clause." }
              },
              {
                question: { text: "What quality standards must my deliverables meet?" },
                answer: { text: "All songs must be professionally mixed and mastered, radio-ready, and meet industry technical standards. The delivery requirements specify exact file formats and quality benchmarks. Substandard work can be rejected, delaying your milestone payments." }
              }
            ]
          },

          // Section 4: Agreement Length
          [IqModeSectionKey.agreementLength]: {
            id: IqModeSectionKey.agreementLength,
            sectionTitle: "Agreement Length",
            questions: [
              {
                question: { text: "How long is the initial contract term?" },
                answer: { text: "The initial term is 2 years from execution, but the label can control your masters for an additional 3 years after that through post-term control provisions. So you're essentially locked in for about 5 years total before potentially getting any rights back." }
              },
              {
                question: { text: "Can the contract be extended beyond the initial term?" },
                answer: { text: "Yes — the label can exercise option periods to extend the term if certain conditions are met, such as album performance targets. Each extension typically adds another 12-18 months of exclusivity under the same exclusivity restrictions." }
              }
            ]
          },

          // Section 5: Liability Safeguards
          [IqModeSectionKey.liabilitySafeguards]: {
            id: IqModeSectionKey.liabilitySafeguards,
            sectionTitle: "Liability & Safeguards",
            questions: [
              {
                question: { text: "What happens if I don't deliver on time?" },
                answer: { text: "Missing deadlines can trigger the Breach Clause, meaning you might have to pay back all unrecouped advances. It's a serious financial risk if you can't meet your delivery obligations." }
              },
              {
                question: { text: "What are the consequences of breaching promotional obligations?" },
                answer: { text: "Failing to comply with the Promotion Participation Clause can lead to payment suspensions, reduced marketing support, or even contract termination through the breach provisions. The label can also reduce future advances if you don't participate in required promotional activities." }
              },
              {
                question: { text: "Am I protected if the label doesn't fulfill their obligations?" },
                answer: { text: "The contract has cure periods allowing both parties to fix violations before facing penalties. However, your leverage is limited during the contract term since the label controls your masters and can withhold payments during disputes." }
              }
            ]
          }
        }
      },

      // Label Perspective
      "Label": {
        sections: {
          // Section 1: Earnings
          [IqModeSectionKey.earnings]: {
            id: IqModeSectionKey.earnings,
            sectionTitle: "Earnings",
            questions: [
              {
                question: { text: "How does the label recoup its investment?" },
                answer: { text: "The label recoups the $420K advance and $95,000 recording costs from the artist's royalty earnings before paying out any additional money. This advance recoupment structure protects the label's investment." },
                ttsSrcUrl: "/audio/TTS Audio/Contract 1/Contract 1 EQ Terms/Contract 1 - EQ Card 1 - Money You_ll Receive.mp3"
              },
              {
                question: { text: "What percentage of revenue does the label keep?" },
                answer: { text: "The label keeps the majority of revenue, with the artist earning percentages defined in the Royalty Rate Table: 18% on sales, 15% on streams, 12.5% on syncs. The label retains 82% on sales, 85% on streams, plus the artist's 3% backend royalty only applies after full recoupment." }
              },
              {
                question: { text: "How are recording costs handled?" },
                answer: { text: "The $95,000 recording costs are advanced upfront but fully recoupable from artist royalties. Any additional costs beyond this amount may also be recouped, depending on budget approvals. This ensures the label's financial risk is minimized." }
              }
            ]
          },

          // Section 2: Quality of Rights
          [IqModeSectionKey.qualityOfRights]: {
            id: IqModeSectionKey.qualityOfRights,
            sectionTitle: "Quality of Rights",
            questions: [
              {
                question: { text: "What rights does the label acquire?" },
                answer: { text: "The label acquires full ownership of all Master Recordings delivered during the term, plus post-term control for an additional three years. This ownership includes distribution, licensing, and all exploitation rights worldwide." }
              },
              {
                question: { text: "Can the artist get their masters back?" },
                answer: { text: "The Reversion Clause allows the artist to request rights reversion after three years post-term, but the label isn't required to agree. In practice, reversion typically only happens if advances are fully recouped and specific conditions are met." }
              },
              {
                question: { text: "What control does the label have over music usage?" },
                answer: { text: "The Usage Control Clause gives the label complete discretion over licensing, sync placements, sampling, and all commercial uses. The artist cannot block or veto any usage decisions during the ownership period." }
              }
            ]
          },

          // Section 3: Usage Obligations
          [IqModeSectionKey.usageObligations]: {
            id: IqModeSectionKey.usageObligations,
            sectionTitle: "Usage & Obligations",
            questions: [
              {
                question: { text: "What must the artist deliver?" },
                answer: { text: "The Delivery Obligation Clause requires 2 EPs with 11 total songs, all professionally mixed and mastered to commercial release standards. Failure to meet these requirements can suspend payments and extend the contract term." }
              },
              {
                question: { text: "How does the exclusivity clause protect the label?" },
                answer: { text: "The Exclusivity Clause prevents the artist from recording for other labels or releasing independent projects during the contract term. This protects the label's investment by ensuring all the artist's creative output flows through this deal." }
              },
              {
                question: { text: "What promotional commitments does the artist have?" },
                answer: { text: "The Promotion Participation Clause requires the artist to participate in marketing campaigns, interviews, social media promotion, and live appearances as directed by the label. Non-compliance can trigger the Breach Clause." }
              }
            ]
          },

          // Section 4: Agreement Length
          [IqModeSectionKey.agreementLength]: {
            id: IqModeSectionKey.agreementLength,
            sectionTitle: "Agreement Length",
            questions: [
              {
                question: { text: "How long does the label control the masters?" },
                answer: { text: "The label controls masters for the 2-year initial term plus three additional years of post-term control. This 5-year total ownership period provides sufficient time to recoup investments and maximize catalog value." }
              }
            ]
          },

          // Section 5: Liability Safeguards
          [IqModeSectionKey.liabilitySafeguards]: {
            id: IqModeSectionKey.liabilitySafeguards,
            sectionTitle: "Liability & Safeguards",
            questions: [
              {
                question: { text: "What happens if the artist breaches the contract?" },
                answer: { text: "The Breach Clause allows the label to suspend payments, demand repayment of unrecouped advances, and potentially terminate the agreement. This protects the label's investment if the artist fails to meet delivery obligations or other requirements." }
              },
              {
                question: { text: "How does the label handle artist disputes?" },
                answer: { text: "During the contract term, the label maintains leverage through master ownership and payment control. Artists who dispute terms are reminded that the reversion clause and future opportunities depend on maintaining good standing with the label." }
              }
            ]
          }
        }
      }
    }
  }
  },

  // CONTRACT 2: Producer Agreement
  {
    contractId: DEMO_CONTRACT_IDS.PRODUCER_AGREEMENT,
    ownerId: DEMO_USER_IDS.DEMO_USER_1,
    name: "Producer Agreement",
    type: 'producer',

    // Contract Extraction - New structured approach replacing old flat properties
    contractExtraction: {
      extractedType: 'producer',
      parties: ["Producer", "Artist"], 
      
      // Basic variables from producer agreement structure
      variables: {
        "upfront_fee": {
          name: "Upfront Fee",
          type: ContractVariableType.discovered_term,
          id: "upfront_fee", 
          value: "$15,000 per track upfront fee",
          confidence: 1.0,
          firstOccurrence: 80,
          context: "Producer shall receive $15,000 per track as upfront fee",
          variations: ["producer fee", "upfront payment"],
          definitionCitation: "Contract Provision - Upfront Fee"
        },
        "backend_royalty": {
          name: "Backend Royalty",
          type: ContractVariableType.discovered_term,
          id: "backend_royalty",
          value: "3% of gross revenue backend royalty",
          confidence: 1.0,
          firstOccurrence: 150,
          context: "Producer entitled to 3% of gross revenue as backend royalty",
          variations: ["producer points", "backend percentage"],
          definitionCitation: "Contract Provision - Backend Royalty"
        },
        "delivery_timeline": {
          name: "Delivery Timeline",
          type: ContractVariableType.discovered_term,
          id: "delivery_timeline", 
          value: "60 days delivery deadline",
          confidence: 1.0,
          firstOccurrence: 300,
          context: "All tracks must be delivered within 60 days of execution",
          variations: ["delivery deadline", "completion time"],
          definitionCitation: "Contract Provision - Delivery"
        },
        "publishing_share": {
          name: "Publishing Share",
          type: ContractVariableType.discovered_term,
          id: "publishing_share",
          value: "25% publishing share",
          confidence: 1.0,
          firstOccurrence: 400,
          context: "Producer receives 25% share of publishing rights",
          variations: ["publishing split", "writer share"],
          definitionCitation: "Contract Provision - Publishing Share"
        }
      },

      // Contract text from producer agreement
      contractTexts: {
        originalText: { 
          text: `This producer agreement establishes terms for the creation of 6 original musical compositions with an upfront fee of $15,000 per track totaling $90,000. Producer shall retain 25% publishing share and receive 3% backend royalty on all gross revenue generated.

All tracks must be delivered within 60 days in 44.1kHz WAV format with complete trackouts and stems. Producer warrants all compositions are original and free of third-party claims. Any late delivery may result in fee reductions as specified herein.

Royalty payments shall only commence upon commercial release of the tracks. All disputes shall be resolved through binding arbitration. Producer acknowledges that tracks may not be used and no kill fee applies if tracks are rejected for commercial release.`
        },
        taggedText: { 
          text: `This producer agreement establishes terms for the creation of 6 original musical compositions with an <variable id="upfront_fee" type="discovered_term" level="1" instance="1">upfront fee</variable> of <variable id="upfront_fee" type="discovered_term" level="1" instance="2">$15,000 per track</variable> totaling $90,000. Producer shall retain <variable id="publishing_share" type="discovered_term" level="1" instance="1">25% publishing share</variable> and receive <variable id="backend_royalty" type="discovered_term" level="1" instance="1">3% backend royalty</variable> on all gross revenue generated.

All tracks must be <variable id="delivery_timeline" type="discovered_term" level="1" instance="1">delivered within 60 days</variable> in 44.1kHz WAV format with complete trackouts and stems. Producer warrants all compositions are original and free of third-party claims. Any <variable id="delivery_timeline" type="discovered_term" level="1" instance="2">late delivery</variable> may result in <variable id="upfront_fee" type="discovered_term" level="1" instance="3">fee reductions</variable> as specified herein.

<variable id="backend_royalty" type="discovered_term" level="1" instance="2">Royalty payments</variable> shall only commence upon commercial release of the tracks. All disputes shall be resolved through binding arbitration. Producer acknowledges that tracks may not be used and no kill fee applies if tracks are rejected for commercial release.`
        }
      }
    },

    eqData: {
      cards: {
        [EqCardKey.moneyYouReceive]: {
          id: EqCardKey.moneyYouReceive,
          title: "Money You Receive",
          type: EqCardType.A,
          cardUniqueData: {
            MONEY_RECEIVED: {
              majorNumber: "$90,000",
              paidAfterList: [
                "Upfront Fee - $15,000 per track",
                "6 tracks - $90,000",
                "Backend Royalty - 3% of gross revenue",
                "Bonus if album goes platinum - $25,000"
              ]
            }
          },
          ttsSrcUrl: "/audio/TTS Audio/Contract 2/EQ Cards/C2-EQ-Card1-Money You_ll Receive.mp3",
          totalAdvance: "$90,000",
          items: [
            {
              title: "Upfront Fee",
              value: "$15,000 per track"
            },
            {
              title: "6 tracks",
              value: "$90,000"
            },
            {
              title: "Backend Royalty",
              value: "3% of gross revenue"
            },
            {
              title: "Bonus if album goes platinum",
              value: "$25,000"
            }
          ]
        },
        [EqCardKey.whatYouOwn]: {
          id: EqCardKey.whatYouOwn,
          title: "What You Own (and Don't)",
          type: EqCardType.A,
          cardUniqueData: {
            OWNERSHIP: {
              ownershipTerms: [
                {
                  title: "Publishing",
                  description: "25%"
                },
                {
                  title: "Masters",
                  description: "Label-owned"
                }
              ]
            }
          },
          ttsSrcUrl: "/audio/TTS Audio/Contract 2/EQ Cards/C2-EQ-Card2-What You Own.mp3",
          items: [
            {
              title: "Masters",
              value: "Label-owned"
            },
            {
              title: "Publishing",
              value: "25"
            }
          ]
        },
        [EqCardKey.whatYoureResponsibleFor]: {
          id: EqCardKey.whatYoureResponsibleFor,
          title: "What You're Responsible For",
          type: EqCardType.A,
          cardUniqueData: {
            RESPONSIBILITIES: {
              responsibilities: [
                {
                  title: "Deliverables",
                  description: "6 completed beats, trackouts, stems"
                },
                {
                  title: "Delivery Time",
                  description: "2 months"
                },
                {
                  title: "Tech Specs",
                  description: "44.1kHz WAV, etc."
                }
              ]
            }
          },
          subTitle: "Deliverables",
          ttsSrcUrl: "/audio/TTS Audio/Contract 2/EQ Cards/C2-EQ-Card3-What You_re Responsible For.mp3",
          items: [
            {
              title: "Deliverables",
              value: "6 completed beats, trackouts, stems"
            },
            {
              title: "Delivery Time",
              value: "2 months"
            },
            {
              title: "Tech Specs",
              value: "44.1kHz WAV, etc."
            }
          ]
        },
        [EqCardKey.howLongThisDealLasts]: {
          id: EqCardKey.howLongThisDealLasts,
          title: "How Long This Deal Lasts",
          type: EqCardType.B,
          cardUniqueData: {
            DURATION: {
              durationType: DurationType.fixed,
              durationText: "60 days delivery deadline, no ongoing obligation, royalty period for life of copyright",
              durationDetails: [
                {
                  title: "Delivery Deadline",
                  description: "60 days"
                },
                {
                  title: "Ongoing Obligation",
                  description: "None after delivery"
                },
                {
                  title: "Royalty Period",
                  description: "Life of copyright"
                }
              ]
            }
          },
          ttsSrcUrl: "/audio/TTS Audio/Contract 2/EQ Cards/C2-EQ-Card4-How Long This Deal Lasts.mp3",
          items: [
            {
              title: "Delivery Deadline",
              value: "60 days"
            },
            {
              title: "Ongoing Obligation",
              value: "None after delivery"
            },
            {
              title: "Royalty Period",
              value: "Life of copyright"
            }
          ]
        },
        [EqCardKey.risksCostsLegalStuff]: {
          id: EqCardKey.risksCostsLegalStuff,
          title: "Risks, Costs & Legal Stuff",
          type: EqCardType.B,
          cardUniqueData: {
            LEGAL: {
              risks: "$95K recording recoup + late delivery fee reductions",
              costs: "No kill fee if tracks not used",
              legal: "Royalty only triggered upon release, arbitration for disputes"
            }
          },
          ttsSrcUrl: "/audio/TTS Audio/Contract 2/EQ Cards/C2-EQ-Card5-Risks, Costs, Legal Stuff.mp3",
          items: [
            {
              title: "Risks",
              value: "$95K recording recoup + late delivery fee reductions"
            },
            {
              title: "Costs",
              value: "No kill fee if tracks not used"
            },
            {
              title: "Liabilities",
              value: "Royalty only triggered upon release, arbitration for disputes"
            }
          ]
        }
      }
    },

    iqData: {
      iqModeData: {
        // Producer Perspective
        "Producer": {
          sections: {
            // Section 1: Earnings
            [IqModeSectionKey.earnings]: {
              id: IqModeSectionKey.earnings,
              sectionTitle: "Earnings",
              questions: [
                {
                  question: { text: "How much will you get paid upfront for this work?" },
                  answer: { text: "You'll receive a $15,000 Upfront Fee per track, totaling $90,000 for all 6 tracks. This payment isn't recoupable, meaning you keep it regardless of how the music performs commercially. The full amount is guaranteed once you meet the delivery requirements." },
                  ttsSrcUrl: "/audio/TTS Audio/Contract 2/EQ Cards/C2-EQ-Card1-Money You_ll Receive.mp3"
                },
                {
                  question: { text: "What backend royalties will you earn?" },
                  answer: { text: "You'll earn a 3% Backend Royalty on gross revenue, but only after the tracks are commercially released. If the label shelves your beats and never releases them, you won't see any backend payments despite having done the work." }
                },
                {
                  question: { text: "Is there any bonus payment structure?" },
                  answer: { text: "Yes - if the album goes platinum, you'll receive an additional $25,000 bonus payment on top of your Upfront Fee and Backend Royalty. However, this is contingent on commercial success and the label actually releasing your tracks." }
                },
                {
                  question: { text: "When do you actually receive the upfront payment?" },
                  answer: { text: "The $90,000 Upfront Fee is typically paid in installments tied to delivery milestones. You might receive partial payment upon signing, with the remainder paid after successful delivery within the 60-day timeline. Late delivery can trigger fee reductions that directly impact this amount." }
                }
              ]
            },

            // Section 2: Quality of Rights
            [IqModeSectionKey.qualityOfRights]: {
              id: IqModeSectionKey.qualityOfRights,
              sectionTitle: "Quality of Rights",
              questions: [
                {
                  question: { text: "Do you own the beats you create?" },
                  answer: { text: "No - the label owns the master recordings of your beats. However, you retain a 25% Publishing Share, which means you'll earn from licensing, sync opportunities, and any time the composition is used. This publishing income can be substantial over the life of the copyright." }
                },
                {
                  question: { text: "What rights do you keep long-term?" },
                  answer: { text: "You keep your producer credit and your 25% Publishing Share permanently. The publishing gives you ongoing income when the music is streamed, sampled, covered by other artists, or licensed for TV/film/commercials. These rights last for the life of copyright." }
                },
                {
                  question: { text: "Can you use these beats elsewhere?" },
                  answer: { text: "No - once delivered, these are exclusive to the label. You can't shop them to other artists or use them in your own projects. This exclusivity starts immediately upon delivery, even if the label never releases the tracks commercially." }
                },
                {
                  question: { text: "What percentage of publishing revenue will you actually receive?" },
                  answer: { text: "Your 25% Publishing Share applies to all publishing income streams: mechanical royalties from streams and sales, performance royalties from radio play, sync licensing for TV/film, and any covers or samples. The label or artist retains the other 75% of publishing." }
                }
              ]
            },

            // Section 3: Usage Obligations
            [IqModeSectionKey.usageObligations]: {
              id: IqModeSectionKey.usageObligations,
              sectionTitle: "Usage & Obligations",
              questions: [
                {
                  question: { text: "What exactly do you need to deliver?" },
                  answer: { text: "6 fully completed beats with trackouts and stems in 44.1kHz WAV format. Each beat needs to be mixed, mastered, and ready for commercial release. You must also provide complete session files with all individual instrument tracks separated." }
                },
                {
                  question: { text: "What's the deadline for delivery?" },
                  answer: { text: "All 6 tracks must be delivered within the 60-day Delivery Timeline from signing. Late delivery can result in fee reductions to your $90,000 Upfront Fee, so meeting this deadline is crucial for full payment. The contract may specify exact penalties per day of delay." }
                },
                {
                  question: { text: "Do you need to clear any samples?" },
                  answer: { text: "You warrant that all beats are 100% original and free of third-party claims. If you use any samples, you're responsible for clearing them before delivery - the label won't handle sample clearance costs or negotiate with rights holders. Uncleared samples can result in delivery rejection and fee forfeiture." }
                },
                {
                  question: { text: "What technical specifications must the deliverables meet?" },
                  answer: { text: "Beyond the 60-day timeline, all tracks must meet professional quality standards: 44.1kHz/24-bit WAV files, complete trackouts with each instrument on separate tracks, properly labeled stems, and commercial-ready mix and master. Substandard work can be rejected, delaying your payment." }
                }
              ]
            },

            // Section 4: Agreement Length
            [IqModeSectionKey.agreementLength]: {
              id: IqModeSectionKey.agreementLength,
              sectionTitle: "Agreement Length",
              questions: [
                {
                  question: { text: "How long do your obligations last?" },
                  answer: { text: "Your main obligation is the 60-day Delivery Timeline. After successful delivery, you have no ongoing production duties except warranty protection if any legal issues arise. Your Backend Royalty rights and 25% Publishing Share last for the life of copyright - potentially 70+ years." }
                },
                {
                  question: { text: "Are there any ongoing obligations after delivery?" },
                  answer: { text: "You remain liable for your originality warranty indefinitely. If someone later claims copyright infringement on beats you delivered, you're responsible for defending against those claims and potentially indemnifying the label. This risk persists even decades after delivery." }
                }
              ]
            },

            // Section 5: Liability Safeguards
            [IqModeSectionKey.liabilitySafeguards]: {
              id: IqModeSectionKey.liabilitySafeguards,
              sectionTitle: "Liability & Safeguards",
              questions: [
                {
                  question: { text: "What happens if you deliver late?" },
                  answer: { text: "Missing the 60-day Delivery Timeline can trigger fee reductions - the exact amount depends on how late you are. This directly impacts your $90,000 Upfront Fee, potentially costing you thousands per day of delay. In extreme cases, the label could reject delivery entirely." }
                },
                {
                  question: { text: "What if the label doesn't use your beats?" },
                  answer: { text: "There's no kill fee, meaning you don't get extra payment if they shelf your work. You keep the Upfront Fee but lose all potential Backend Royalty income if tracks never get released. This is a significant risk since you have no control over the label's release decisions." }
                },
                {
                  question: { text: "How are disputes handled?" },
                  answer: { text: "All disputes go to binding arbitration rather than court. This can be faster and cheaper than litigation, but you give up your right to a jury trial and may have limited appeal options if the arbitrator rules against you." }
                },
                {
                  question: { text: "What liability do you face for sample clearance issues?" },
                  answer: { text: "You warrant all beats are original and free of third-party claims. If someone later proves you used uncleared samples, you could be liable for damages, legal fees, and forced to return your Upfront Fee. This financial exposure can far exceed your $90,000 payment if the claim is substantial." }
                }
              ]
            }
          }
        },

        // Artist/Label Perspective
        "Artist": {
          sections: {
            // Section 1: Earnings
            [IqModeSectionKey.earnings]: {
              id: IqModeSectionKey.earnings,
              sectionTitle: "Earnings",
              questions: [
                {
                  question: { text: "What does the producer upfront payment cost you?" },
                  answer: { text: "You'll pay the producer a $15,000 Upfront Fee per track, totaling $90,000 for all 6 tracks. This cost is non-recoupable from producer royalties but is typically recouped from your artist royalties if you're signed to a label." },
                  ttsSrcUrl: "/audio/TTS Audio/Contract 2/EQ Cards/C2-EQ-Card1-Money You_ll Receive.mp3"
                },
                {
                  question: { text: "What ongoing royalties must you pay the producer?" },
                  answer: { text: "The producer receives a 3% Backend Royalty on gross revenue from these tracks. This comes directly off the top before calculating your artist royalties, reducing your overall earnings on any project using these beats." }
                },
                {
                  question: { text: "Are there additional costs beyond the base fee?" },
                  answer: { text: "Beyond the $90,000 Upfront Fee and 3% Backend Royalty, you may owe a $25,000 platinum bonus if the album achieves that milestone. You're also responsible for any revision requests or additional production work not covered in the original scope." }
                }
              ]
            },

            // Section 2: Quality of Rights
            [IqModeSectionKey.qualityOfRights]: {
              id: IqModeSectionKey.qualityOfRights,
              sectionTitle: "Quality of Rights",
              questions: [
                {
                  question: { text: "What rights do you acquire?" },
                  answer: { text: "You acquire ownership of the master recordings but must share 25% of publishing with the producer. This 25% Publishing Share means the producer earns from all sync licensing, streaming, and any other uses of the composition - reducing your publishing income permanently." }
                },
                {
                  question: { text: "Can you use the beats however you want?" },
                  answer: { text: "Yes - you own the masters and can use them for albums, singles, promotional content, or any commercial purpose. However, the producer's 25% Publishing Share and 3% Backend Royalty apply to all uses, limiting your creative control financially." }
                },
                {
                  question: { text: "What happens if you never release the tracks?" },
                  answer: { text: "You still owe the full $90,000 Upfront Fee even if you never release the beats commercially. There's no kill fee provision protecting you, so shelving the project means you've paid for tracks that generate no revenue but the producer keeps all upfront money." }
                }
              ]
            },

            // Section 3: Usage Obligations
            [IqModeSectionKey.usageObligations]: {
              id: IqModeSectionKey.usageObligations,
              sectionTitle: "Usage & Obligations",
              questions: [
                {
                  question: { text: "What can you expect the producer to deliver?" },
                  answer: { text: "The producer must deliver 6 completed beats with full trackouts and stems within the 60-day Delivery Timeline. Each track should be professionally mixed, mastered, and ready for commercial release with all individual elements separated for maximum creative flexibility." }
                },
                {
                  question: { text: "What happens if the producer delivers late?" },
                  answer: { text: "Late delivery beyond the 60-day timeline allows you to reduce the Upfront Fee based on contract penalties. However, this doesn't compensate for potential revenue loss if your release schedule gets delayed or market opportunities are missed." }
                },
                {
                  question: { text: "What quality standards can you enforce?" },
                  answer: { text: "The contract specifies technical requirements: 44.1kHz WAV format with complete trackouts and stems. You can reject beats that don't meet professional commercial standards, but this extends the Delivery Timeline and delays your project without guarantee the revisions will meet expectations." }
                }
              ]
            },

            // Section 4: Agreement Length
            [IqModeSectionKey.agreementLength]: {
              id: IqModeSectionKey.agreementLength,
              sectionTitle: "Agreement Length",
              questions: [
                {
                  question: { text: "How long do you pay the producer royalties?" },
                  answer: { text: "The producer's 3% Backend Royalty and 25% Publishing Share last for the life of copyright - potentially 70+ years. Every time these tracks earn money, the producer gets their cut indefinitely, regardless of how successful your career becomes later." }
                }
              ]
            },

            // Section 5: Liability Safeguards
            [IqModeSectionKey.liabilitySafeguards]: {
              id: IqModeSectionKey.liabilitySafeguards,
              sectionTitle: "Liability & Safeguards",
              questions: [
                {
                  question: { text: "What protections do you have against sample issues?" },
                  answer: { text: "The producer warrants all beats are original and must indemnify you against copyright claims. However, even with this warranty, you could face legal battles, injunctions preventing release, or demands to pull music from streaming if uncleared samples surface. The producer may lack resources to fully cover major claims." }
                },
                {
                  question: { text: "How are disputes resolved?" },
                  answer: { text: "Disputes go to binding arbitration rather than court. While potentially faster, arbitration limits your legal options and may favor producers who are more familiar with the process. You cannot appeal unfavorable rulings in most cases." }
                },
                {
                  question: { text: "What happens if tracks don't meet your creative vision?" },
                  answer: { text: "There's no kill fee provision, meaning you can't pay a penalty to walk away from beats you don't like. Once the 60-day Delivery Timeline is met with technically compliant work, you owe the full $90,000 Upfront Fee regardless of creative fit or commercial viability." }
                }
              ]
            }
          }
        }
      }
    }
  },

  // CONTRACT 3: Brand Partnership / NIL Deal
  {
    contractId: DEMO_CONTRACT_IDS.BRAND_PARTNERSHIP_NIL,
    ownerId: DEMO_USER_IDS.DEMO_USER_1,
    name: "Brand Partnership / NIL Deal",
    type: 'services',

    // Contract Extraction - New structured approach replacing old flat properties
    contractExtraction: {
      extractedType: 'services',
      parties: ["Influencer", "Brand"], 
      
      // Basic variables from brand partnership structure
      variables: {
        "total_deal_value": {
          name: "Total Deal Value",
          type: ContractVariableType.discovered_term,
          id: "total_deal_value", 
          value: "$75,000 total compensation",
          confidence: 1.0,
          firstOccurrence: 90,
          context: "Total compensation shall be $75,000 paid in installments",
          variations: ["deal value", "total compensation"],
          definitionCitation: "Contract Provision - Total Deal Value"
        },
        "campaign_term": {
          name: "Campaign Term",
          type: ContractVariableType.discovered_term,
          id: "campaign_term",
          value: "3 months active campaign period",
          confidence: 1.0,
          firstOccurrence: 200,
          context: "Campaign period shall run for 3 months from launch date",
          variations: ["campaign duration", "active period"],
          definitionCitation: "Contract Provision - Campaign"
        },
        "content_deliverables": {
          name: "Content Deliverables",
          type: ContractVariableType.discovered_term,
          id: "content_deliverables", 
          value: "2 Instagram posts, 1 TikTok, 1 panel appearance",
          confidence: 1.0,
          firstOccurrence: 300,
          context: "Influencer shall deliver 2 IG posts, 1 TikTok video, and 1 panel appearance",
          variations: ["content requirements", "deliverables"],
          definitionCitation: "Contract Provision - Influencer"
        },
        "usage_rights": {
          name: "Usage Rights Period",
          type: ContractVariableType.discovered_term,
          id: "usage_rights",
          value: "12 months usage rights from launch",
          confidence: 1.0,
          firstOccurrence: 450,
          context: "Brand retains usage rights for 12 months from campaign launch",
          variations: ["usage period", "license duration"],
          definitionCitation: "Contract Provision - Usage Rights"
        }
      },

      // Contract text from brand partnership
      contractTexts: {
        originalText: { 
          text: `This influencer partnership agreement establishes a 3-month campaign for total compensation of $75,000 paid in installments. Influencer agrees to create 2 Instagram posts, 1 TikTok video, and participate in 1 panel appearance.

Brand receives 12-month usage rights for social media and advertising purposes only. Influencer retains IP ownership of all created content, but brand may use content without additional approval during the usage period. All content must comply with brand messaging guidelines and include required hashtags.

Influencer warrants compliance with FTC disclosure requirements and maintains right to approve final usage. Campaign must meet posting deadlines and brand reserves right to suspend partnership for controversy. Legal indemnity is capped at total deal value of $75,000.

Usage rights expire 13 months from launch with sunset clause requiring brand to remove all assets. Influencer maintains control over content alteration and final approval rights.`
        },
        taggedText: { 
          text: `This influencer partnership agreement establishes a <variable id="campaign_term" type="discovered_term" level="1" instance="1">3-month campaign</variable> for <variable id="total_deal_value" type="discovered_term" level="1" instance="1">total compensation of $75,000</variable> paid in installments. Influencer agrees to create <variable id="content_deliverables" type="discovered_term" level="1" instance="1">2 Instagram posts, 1 TikTok video, and participate in 1 panel appearance</variable>.

Brand receives <variable id="usage_rights" type="discovered_term" level="1" instance="1">12-month usage rights</variable> for social media and advertising purposes only. Influencer retains IP ownership of all created content, but brand may use content without additional approval during the <variable id="usage_rights" type="discovered_term" level="1" instance="2">usage period</variable>. All content must comply with brand messaging guidelines and include required hashtags.

Influencer warrants compliance with FTC disclosure requirements and maintains right to approve final usage. <variable id="campaign_term" type="discovered_term" level="1" instance="2">Campaign</variable> must meet posting deadlines and brand reserves right to suspend partnership for controversy. Legal indemnity is capped at <variable id="total_deal_value" type="discovered_term" level="1" instance="2">total deal value of $75,000</variable>.

<variable id="usage_rights" type="discovered_term" level="1" instance="3">Usage rights</variable> expire 13 months from launch with sunset clause requiring brand to remove all assets. Influencer maintains control over <variable id="content_deliverables" type="discovered_term" level="1" instance="2">content</variable> alteration and final approval rights.`
        }
      }
    },

    eqData: {
      cards: {
        [EqCardKey.moneyYouReceive]: {
          id: EqCardKey.moneyYouReceive,
          title: "Money You Receive",
          type: EqCardType.A,
          cardUniqueData: {
            MONEY_RECEIVED: {
              majorNumber: "$75,000",
              paidAfterList: [
                "Total Deal Value - $75,000",
                "Signing - $30K",
                "First post live - $15K",
                "Campaign complete - $30K"
              ]
            }
          },
          ttsSrcUrl: "/audio/TTS Audio/Contract 3/C3-EQ-Card1-Money.mp3",
          items: [
            {
              title: "Total Deal Value",
              value: "$75,000"
            },
            {
              title: "Signing",
              value: "$30K"
            },   
            {
              title: "First post live",
              value: "$15K"
            },
            {
              title: "Campaign complete",
              value: "$30K"
            }
          ]
        },
        [EqCardKey.whatYouOwn]: {
          id: EqCardKey.whatYouOwn,
          title: "What You Own (and Don't)",
          type: EqCardType.B,
          cardUniqueData: {
            OWNERSHIP: {
              ownershipTerms: [
                {
              title: "NIL Licensed",
              description: "Name, Image, Likeness licensed to brand"
            },
            {
              title: "Usage rights",
              description: "1 year, social + ads only"
            },
            {
              title: "Content Control",
              description: "Brand cannot alter original content without approval"
            },
            {
              title: "IP Ownership",
              description: "Artist retains IP of content created"
            }
          ]
            }
          },
          ttsSrcUrl: "/audio/TTS Audio/Contract 3/C3-EQ-Card2-What You Own.mp3",
          items: [
            {
              title: "NIL Licensed",
              value: "Name, Image, Likeness licensed to brand"
            },
            {
              title: "Usage rights",
              value: "1 year, social + ads only"
            },
            {
              title: "Content Control",
              value: "Brand cannot alter original content without approval"
            },
            {
              title: "IP Ownership",
              value: "Artist retains IP of content created"
            }
          ]
        },
        [EqCardKey.whatYoureResponsibleFor]: {
          id: EqCardKey.whatYoureResponsibleFor,
          title: "What You're Responsible For",
          type: EqCardType.A,
          cardUniqueData: {
            RESPONSIBILITIES: {
              responsibilities: [
                {
                  title: "Social Content",
                  description: "2 IG posts, 1 TikTok"
                },
                {
                  title: "Panel Appearances",
                  description: "1 panel appearance"
                },
                {
                  title: "Brand Compliance",
                  description: "Brand hashtags + messaging compliance"
                }
              ]
            }
          },
          subTitle: "Deliverables",
          ttsSrcUrl: "/audio/TTS Audio/Contract 3/C3-EQ-Card3-Responsible.mp3",
          items: [
            {
              title: "Social Content",
              value: "2 IG posts, 1 TikTok"
            },
            {
              title: "Panel Appearances",
              value: "1 panel appearance"
            },
            {
              title: "Brand Compliance",
              value: "Brand hashtags + messaging compliance"
            }
          ]
        },
        [EqCardKey.howLongThisDealLasts]: {
          id: EqCardKey.howLongThisDealLasts,
          title: "How Long This Deal Lasts",
          type: EqCardType.B,
          cardUniqueData: {
            DURATION: {
              durationType: DurationType.fixed,
              durationText: "3 months campaign term, 12 months usage rights, sunset clause at Month 13",
              durationDetails: [
                {
                  title: "Campaign Term",
                  description: "3 months"
                },
                {
                  title: "Usage Rights",
                  description: "12 months from launch date"
                },
                {
                  title: "Sunset Clause",
                  description: "Brand must remove all assets by Month 13"
                }
              ]
            }
          },
          ttsSrcUrl: "/audio/TTS Audio/Contract 3/C3-EQ-Card4-How Long.mp3",
          items: [
            {
              title: "Campaign Term",
              value: "3 months"
            },
            {
              title: "Usage Rights",
              value: "12 months from launch date"
            },
            {
              title: "Sunset Clause",
              value: "Brand must remove all assets by Month 13"
            }
          ]
        },
        [EqCardKey.risksCostsLegalStuff]: {
          id: EqCardKey.risksCostsLegalStuff,
          title: "Risks, Costs & Legal Stuff",
          type: EqCardType.A,
          cardUniqueData: {
            LEGAL: {
              risks: "Must meet posting deadlines, controversy suspension possible",
              costs: "Creative approval required before posts go live",
              legal: "Legal indemnity capped at total deal value ($75K)"
            }
          },
          ttsSrcUrl: "/audio/TTS Audio/Contract 3/C3-EQ-Card5-Legal.mp3",
          items: [
            {
              title: "Risks",
              value: "Must meet posting deadlines, controversy suspension possible"
            },
            {
              title: "Costs",
              value: "Creative approval required before posts go live"
            },
            {
              title: "Liabilities",
              value: "Legal indemnity capped at total deal value ($75K)"
            }
          ]
        }
      }
    },

    iqData: {
      iqModeData: {
        // Influencer Perspective
        "Influencer": {
          sections: {
            // Section 1: Earnings
            [IqModeSectionKey.earnings]: {
              id: IqModeSectionKey.earnings,
              sectionTitle: "Earnings",
              questions: [
                {
                  question: { text: "How much will you earn from this partnership?" },
                  answer: { text: "You'll earn a $75,000 Total Deal Value - $30K at signing, $15K after your first post goes live, and $30K when the campaign is complete during the 3-month Campaign Term. This is guaranteed payment regardless of post performance metrics like views or engagement." },
                  ttsSrcUrl: "/audio/TTS Audio/Contract 3/C3-EQ-Card1-Money.mp3"
                },
                {
                  question: { text: "Are there any performance bonuses or penalties?" },
                  answer: { text: "No performance bonuses tied to views or engagement. However, missing posting deadlines could jeopardize your final $30K payment. The brand can suspend the partnership for controversies, which would stop remaining payments from the $75,000 Total Deal Value." }
                },
                {
                  question: { text: "When do you get paid?" },
                  answer: { text: "Payments from the $75,000 Total Deal Value are milestone-based: $30K upfront upon signing, $15K after first content goes live, and final $30K upon campaign completion. This structure protects the brand while ensuring you receive most money upfront before the heavy lifting begins." }
                },
                {
                  question: { text: "What happens to payment if the brand doesn't approve your content?" },
                  answer: { text: "If the brand rejects your content submissions, you'll need to revise until approved to receive milestone payments. Extended rejection delays could push back the $15K and final $30K payments beyond the 3-month Campaign Term, potentially impacting your cashflow." }
                }
              ]
            },

            // Section 2: Quality of Rights
            [IqModeSectionKey.qualityOfRights]: {
              id: IqModeSectionKey.qualityOfRights,
              sectionTitle: "Quality of Rights",
              questions: [
                {
                  question: { text: "Do you keep ownership of the content you create?" },
                  answer: { text: "Yes - you retain IP ownership of all content you create. However, the brand gets a 12-month Usage Rights Period for social media and advertising, meaning they can repost and promote your content during that period without paying additional fees or seeking permission for each use." }
                },
                {
                  question: { text: "Can the brand change or edit your content?" },
                  answer: { text: "No - the brand cannot alter your original content without your approval. You maintain creative control and final approval rights over how your content is used during the 12-month Usage Rights Period. This protects your brand identity and prevents misrepresentation." }
                },
                {
                  question: { text: "How long can they use your content?" },
                  answer: { text: "The brand can use your content for the 12-month Usage Rights Period from launch date, but there's a sunset clause requiring them to remove all assets by Month 13. After that, they lose all usage rights and must cease using your NIL in all marketing materials." }
                },
                {
                  question: { text: "What happens to content after the usage period expires?" },
                  answer: { text: "After the 12-month Usage Rights Period expires, the brand must remove all content featuring your NIL from their channels. However, organic posts and user-generated content may remain online. You regain full control and could potentially license the same content to competitors." }
                }
              ]
            },

            // Section 3: Usage Obligations
            [IqModeSectionKey.usageObligations]: {
              id: IqModeSectionKey.usageObligations,
              sectionTitle: "Usage & Obligations",
              questions: [
                {
                  question: { text: "What content do you need to create?" },
                  answer: { text: "The Content Deliverables require 2 Instagram posts, 1 TikTok video, and participation in 1 panel appearance during the 3-month Campaign Term. All content must include brand hashtags and comply with their messaging guidelines, which limits your creative freedom." }
                },
                {
                  question: { text: "Do you need pre-approval before posting?" },
                  answer: { text: "Yes - all creative content requires brand approval before going live. This means you'll need to submit drafts and wait for approval, which could delay your posting timeline and extend the Campaign Term if revisions are needed. You cannot post without explicit brand sign-off." }
                },
                {
                  question: { text: "Are there FTC disclosure requirements?" },
                  answer: { text: "Yes - you must comply with all FTC disclosure requirements for sponsored content throughout the Campaign Term. This typically means using #ad or #sponsored hashtags and clearly indicating the partnership. Non-compliance could expose you to legal liability exceeding the $75,000 Total Deal Value cap." }
                },
                {
                  question: { text: "Can you post similar content for competing brands?" },
                  answer: { text: "The contract likely includes exclusivity provisions during the 3-month Campaign Term and potentially extending through the 12-month Usage Rights Period. You may be restricted from partnering with direct competitors, limiting other earning opportunities in the same product category." }
                }
              ]
            },

            // Section 4: Agreement Length
            [IqModeSectionKey.agreementLength]: {
              id: IqModeSectionKey.agreementLength,
              sectionTitle: "Agreement Length",
              questions: [
                {
                  question: { text: "How long is the active campaign period?" },
                  answer: { text: "The 3-month Campaign Term is your active period for creating and posting Content Deliverables. However, the brand's 12-month Usage Rights Period extends well beyond, meaning they can continue using your content for 9 additional months after you've completed all work." }
                },
                {
                  question: { text: "Are there any ongoing obligations after the campaign ends?" },
                  answer: { text: "After the 3-month Campaign Term, you have no obligation to create new content. However, you must maintain the partnership's reputation during the full 12-month Usage Rights Period, as the brand can still suspend for controversy even after you've completed all Content Deliverables." }
                }
              ]
            },

            // Section 5: Liability Safeguards
            [IqModeSectionKey.liabilitySafeguards]: {
              id: IqModeSectionKey.liabilitySafeguards,
              sectionTitle: "Liability & Safeguards",
              questions: [
                {
                  question: { text: "What happens if you miss posting deadlines?" },
                  answer: { text: "Missing deadlines within the 3-month Campaign Term could jeopardize your final $30K payment and potentially allow the brand to terminate the partnership early. Staying on schedule is crucial for receiving the full $75,000 Total Deal Value." }
                },
                {
                  question: { text: "Can the brand cancel due to controversy?" },
                  answer: { text: "Yes - the brand can suspend the partnership if you're involved in any controversy that could harm their reputation, even during the 12-month Usage Rights Period after Content Deliverables are complete. This would stop remaining payments and potentially trigger early termination with demands to return already-paid amounts." }
                },
                {
                  question: { text: "What's your legal liability exposure?" },
                  answer: { text: "Your legal indemnity is capped at the $75,000 Total Deal Value, which limits your financial risk. This is relatively protective compared to unlimited liability clauses. However, FTC violations or reputation harm could expose you to separate regulatory fines beyond this cap." }
                },
                {
                  question: { text: "What protections do you have if the brand misuses your content?" },
                  answer: { text: "You maintain final approval rights during the 12-month Usage Rights Period, but enforcement can be difficult. If the brand violates usage terms, you'd need to pursue legal action, which may cost more than the $75,000 Total Deal Value. The contract should specify remedies for unauthorized content alteration or use beyond the usage period." }
                }
              ]
            }
          }
        },

        // Brand Perspective
        "Brand": {
          sections: {
            // Section 1: Earnings
            [IqModeSectionKey.earnings]: {
              id: IqModeSectionKey.earnings,
              sectionTitle: "Earnings",
              questions: [
                {
                  question: { text: "What is the total cost of this partnership?" },
                  answer: { text: "You'll invest a $75,000 Total Deal Value - $30K at signing, $15K after first content goes live, and $30K upon campaign completion. This payment structure allows you to reduce financial risk by withholding final payment until the influencer completes the 3-month Campaign Term." },
                  ttsSrcUrl: "/audio/TTS Audio/Contract 3/C3-EQ-Card1-Money.mp3"
                },
                {
                  question: { text: "Can you reduce costs if content underperforms?" },
                  answer: { text: "No - the $75,000 Total Deal Value is guaranteed regardless of post performance metrics. Even if content gets poor engagement or doesn't drive conversions, you owe the full amount once Content Deliverables are submitted and approved. There are no performance clawbacks." }
                },
                {
                  question: { text: "What additional costs should you budget beyond the deal value?" },
                  answer: { text: "Beyond the $75,000 Total Deal Value, budget for approval process overhead, potential revision requests, event logistics for the panel appearance, and content amplification costs. You may also need legal review and monitoring during the 12-month Usage Rights Period." }
                }
              ]
            },

            // Section 2: Quality of Rights
            [IqModeSectionKey.qualityOfRights]: {
              id: IqModeSectionKey.qualityOfRights,
              sectionTitle: "Quality of Rights",
              questions: [
                {
                  question: { text: "What rights do you acquire for the $75K investment?" },
                  answer: { text: "You acquire a 12-month Usage Rights Period for social media and advertising only. The influencer retains IP ownership, meaning you cannot use content beyond these channels or extend usage past 12 months without renegotiating and paying additional fees." }
                },
                {
                  question: { text: "Can you modify influencer content for different platforms?" },
                  answer: { text: "No - you cannot alter original content without influencer approval. This limits your ability to optimize content for different platforms or adapt messaging as campaign needs evolve during the Usage Rights Period. Every modification requires negotiation and sign-off." }
                },
                {
                  question: { text: "What happens to your marketing assets after the usage period?" },
                  answer: { text: "After the 12-month Usage Rights Period expires, you must remove all influencer content from your channels due to the sunset clause. This means losing valuable marketing assets and potentially disrupting ongoing campaigns that are performing well." }
                },
                {
                  question: { text: "Can you use content in ways not specified in the agreement?" },
                  answer: { text: "No - usage is strictly limited to social media and advertising during the 12-month Usage Rights Period. Using content for product packaging, in-store displays, or other channels not specified could violate the agreement and expose you to legal action, potentially costing far more than the $75,000 Total Deal Value." }
                }
              ]
            },

            // Section 3: Usage Obligations
            [IqModeSectionKey.usageObligations]: {
              id: IqModeSectionKey.usageObligations,
              sectionTitle: "Usage & Obligations",
              questions: [
                {
                  question: { text: "What deliverables can you expect?" },
                  answer: { text: "The influencer must provide Content Deliverables: 2 Instagram posts, 1 TikTok video, and 1 panel appearance during the 3-month Campaign Term. All content must meet your brand guidelines and include required hashtags, but you cannot guarantee quality or creative execution will meet expectations." }
                },
                {
                  question: { text: "How much control do you have over content approval?" },
                  answer: { text: "You have approval rights before content goes live, allowing you to reject submissions that don't meet brand standards. However, extended approval cycles or multiple revision requests could delay the Campaign Term and damage the influencer relationship, potentially resulting in lower-quality final content." }
                },
                {
                  question: { text: "What happens if the influencer's content doesn't align with your brand?" },
                  answer: { text: "Even with approval rights, you're locked into the $75,000 Total Deal Value once Content Deliverables are technically complete, even if creative execution is disappointing. You cannot terminate for subjective quality concerns, meaning you might pay full price for content that doesn't resonate with your target audience." }
                },
                {
                  question: { text: "Are there exclusivity protections?" },
                  answer: { text: "The contract should include exclusivity provisions preventing the influencer from working with competitors during the Campaign Term and potentially throughout the 12-month Usage Rights Period. However, enforcement can be difficult if not explicitly detailed, and you may see your partner promoting rival brands simultaneously." }
                }
              ]
            },

            // Section 4: Agreement Length
            [IqModeSectionKey.agreementLength]: {
              id: IqModeSectionKey.agreementLength,
              sectionTitle: "Agreement Length",
              questions: [
                {
                  question: { text: "How long can you leverage the partnership?" },
                  answer: { text: "You can actively use content during the 12-month Usage Rights Period, but must create and receive all Content Deliverables within the 3-month Campaign Term. This means 9 months of passive asset usage without additional content creation, which may feel stale as trends evolve." }
                },
                {
                  question: { text: "Can you extend usage rights beyond 12 months?" },
                  answer: { text: "Not without renegotiating and paying additional fees beyond the $75,000 Total Deal Value. The sunset clause forces you to remove all assets after the 12-month Usage Rights Period, potentially disrupting successful campaigns and requiring new partnerships or content creation at additional cost." }
                }
              ]
            },

            // Section 5: Liability Safeguards
            [IqModeSectionKey.liabilitySafeguards]: {
              id: IqModeSectionKey.liabilitySafeguards,
              sectionTitle: "Liability & Safeguards",
              questions: [
                {
                  question: { text: "What recourse do you have if the influencer underdelivers?" },
                  answer: { text: "You can withhold milestone payments if Content Deliverables aren't submitted during the 3-month Campaign Term, but this is your only financial leverage. If content is technically complete but creatively disappointing, you have no recourse to reduce the $75,000 Total Deal Value or terminate early." }
                },
                {
                  question: { text: "What happens if the influencer becomes controversial?" },
                  answer: { text: "You can suspend the partnership for controversy and stop remaining payments, protecting your brand reputation. However, content already posted remains online, and you may still owe payments for completed milestones. The reputational damage could far exceed the financial investment." }
                },
                {
                  question: { text: "What are your liability risks?" },
                  answer: { text: "Influencer legal indemnity is capped at $75,000, meaning if their content causes major legal issues or FTC violations, you could face liability exceeding this amount. You also risk reputational harm, customer backlash, and regulatory scrutiny that financial caps cannot protect against." }
                },
                {
                  question: { text: "How do you enforce usage terms after the campaign ends?" },
                  answer: { text: "During the 12-month Usage Rights Period, the influencer maintains final approval rights, requiring ongoing relationship management. After expiration, ensuring content removal requires monitoring and potential legal action. Former partners may not prioritize your requests, leading to continued unauthorized usage." }
                }
              ]
            }
          }
        }
      }
    }
  },

  // CONTRACT 4: SAFE Agreement
  {
    contractId: DEMO_CONTRACT_IDS.SAFE_AGREEMENT,
    ownerId: DEMO_USER_IDS.DEMO_USER_1,
    name: "SAFE Agreement",
    type: 'services',

    // Contract Extraction
    contractExtraction: {
      extractedType: 'services',
      parties: ["Investor", "Company"],

      // Variables with proper citations to actual sections
      variables: {
        "purchase_amount": {
          name: "Purchase Amount",
          type: ContractVariableType.discovered_term,
          id: "purchase_amount",
          value: "$100,000 investment amount",
          confidence: 1.0,
          firstOccurrence: 450,
          context: "Investor agrees to invest One Hundred Thousand Dollars ($100,000) in exchange for the right to receive equity",
          variations: ["purchase amount", "investment amount", "invested capital"],
          definitionCitation: "Contract Provision - Investment Amount"
        },
        "valuation_cap": {
          name: "Valuation Cap",
          type: ContractVariableType.discovered_term,
          id: "valuation_cap",
          value: "$5,000,000 maximum valuation",
          confidence: 1.0,
          firstOccurrence: 680,
          context: "conversion price shall be calculated using a pre-money valuation cap of Five Million Dollars ($5,000,000)",
          variations: ["valuation cap", "cap", "maximum valuation"],
          definitionCitation: "Contract Provision - Valuation Cap"
        },
        "discount_rate": {
          name: "Discount Rate",
          type: ContractVariableType.discovered_term,
          id: "discount_rate",
          value: "20% discount on conversion price",
          confidence: 1.0,
          firstOccurrence: 850,
          context: "Investor shall receive a twenty percent (20%) discount to the price per share paid by other investors",
          variations: ["discount", "discount rate", "pricing discount"],
          definitionCitation: "Contract Provision - Discount Rate"
        },
        "liquidity_event": {
          name: "Liquidity Event",
          type: ContractVariableType.discovered_term,
          id: "liquidity_event",
          value: "Qualified financing, acquisition, or IPO trigger",
          confidence: 0.95,
          firstOccurrence: 1100,
          context: "A Liquidity Event means (i) a Qualified Financing, (ii) a Change of Control, or (iii) an Initial Public Offering",
          variations: ["liquidity event", "triggering event", "conversion trigger"],
          definitionCitation: "Section 1.3 - Liquidity Event"
        },
        "qualified_financing": {
          name: "Qualified Financing",
          type: ContractVariableType.discovered_term,
          id: "qualified_financing",
          value: "Equity financing of at least $1,000,000",
          confidence: 0.95,
          firstOccurrence: 300,
          context: "Qualified Financing means the Company's issuance and sale of equity securities with aggregate gross proceeds of at least One Million Dollars ($1,000,000)",
          variations: ["qualified financing", "priced round", "equity financing"],
          definitionCitation: "Section 1.1 - Qualified Financing"
        },
        "conversion_trigger": {
          name: "Conversion Trigger",
          type: ContractVariableType.discovered_term,
          id: "conversion_trigger",
          value: "Automatic conversion upon qualifying event",
          confidence: 1.0,
          firstOccurrence: 1250,
          context: "This SAFE will automatically convert into equity upon the occurrence of a Liquidity Event",
          variations: ["conversion", "automatic conversion", "conversion trigger"],
          definitionCitation: "Section 3.1 - Automatic Conversion"
        },
        "pro_rata_rights": {
          name: "Pro Rata Rights",
          type: ContractVariableType.discovered_term,
          id: "pro_rata_rights",
          value: "Right to maintain ownership percentage in future rounds",
          confidence: 0.9,
          firstOccurrence: 1550,
          context: "Investor shall have the right to participate pro rata in future equity financings to maintain their percentage ownership",
          variations: ["pro rata rights", "participation rights", "follow-on rights"],
          definitionCitation: "Section 3.3 - Pro Rata Participation Rights"
        },
        "pro_rata_threshold": {
          name: "Pro Rata Threshold",
          type: ContractVariableType.discovered_term,
          id: "pro_rata_threshold",
          value: "Minimum $50,000 investment required",
          confidence: 0.9,
          firstOccurrence: 1620,
          context: "Pro rata rights shall only apply if Investor's converted equity represents an investment of at least Fifty Thousand Dollars ($50,000)",
          variations: ["minimum investment", "threshold amount", "pro rata minimum"],
          definitionCitation: "Contract Provision - Pro Rata Threshold"
        },
        "information_rights": {
          name: "Information Rights",
          type: ContractVariableType.discovered_term,
          id: "information_rights",
          value: "Quarterly financial statements and annual reports",
          confidence: 0.95,
          firstOccurrence: 1800,
          context: "Company shall deliver to Investor unaudited quarterly financial statements within thirty (30) days of each quarter end and annual audited financials within ninety (90) days of year end",
          variations: ["information rights", "reporting requirements", "financial disclosure"],
          definitionCitation: "Section 4.1 - Information Rights"
        },
        "notice_period": {
          name: "Notice Period",
          type: ContractVariableType.discovered_term,
          id: "notice_period",
          value: "30 days advance notice for material changes",
          confidence: 1.0,
          firstOccurrence: 1950,
          context: "Company shall provide Investor with thirty (30) days prior written notice of any proposed Liquidity Event or material amendment to the Certificate of Incorporation",
          variations: ["notice period", "advance notice", "prior notice"],
          definitionCitation: "Section 4.2 - Notice Requirements"
        },
        "dissolution_priority": {
          name: "Dissolution Priority",
          type: ContractVariableType.discovered_term,
          id: "dissolution_priority",
          value: "Priority claim equal to Purchase Amount in dissolution",
          confidence: 0.95,
          firstOccurrence: 2150,
          context: "Upon a dissolution event, Investor shall be entitled to receive an amount equal to the Purchase Amount prior to any distribution to common stockholders",
          variations: ["dissolution rights", "liquidation preference", "dissolution priority"],
          definitionCitation: "Section 4.3 - Dissolution"
        },
        "assignment_restrictions": {
          name: "Assignment Restrictions",
          type: ContractVariableType.discovered_term,
          id: "assignment_restrictions",
          value: "Transfer only with Company consent or to affiliates",
          confidence: 0.9,
          firstOccurrence: 2350,
          context: "Investor may not transfer this SAFE without the prior written consent of the Company, except to Investor's affiliates or family members",
          variations: ["assignment", "transfer restrictions", "assignability"],
          definitionCitation: "Section 5.1 - Assignment"
        },
        "amendment_requirements": {
          name: "Amendment Requirements",
          type: ContractVariableType.discovered_term,
          id: "amendment_requirements",
          value: "Written consent required from both parties",
          confidence: 1.0,
          firstOccurrence: 2500,
          context: "This SAFE may only be amended with the written consent of both the Company and the Investor",
          variations: ["amendment", "modifications", "changes"],
          definitionCitation: "Section 5.2 - Amendments"
        },
        "governing_law": {
          name: "Governing Law",
          type: ContractVariableType.discovered_term,
          id: "governing_law",
          value: "Delaware law governs agreement",
          confidence: 1.0,
          firstOccurrence: 2650,
          context: "This SAFE shall be governed by and construed in accordance with the laws of the State of Delaware",
          variations: ["governing law", "applicable law", "jurisdiction"],
          definitionCitation: "Section 5.3 - Governing Law"
        },
        "dispute_resolution": {
          name: "Dispute Resolution",
          type: ContractVariableType.discovered_term,
          id: "dispute_resolution",
          value: "Binding arbitration in Delaware",
          confidence: 0.95,
          firstOccurrence: 2750,
          context: "Any dispute arising out of this SAFE shall be resolved through binding arbitration in Delaware in accordance with the rules of the American Arbitration Association",
          variations: ["dispute resolution", "arbitration", "dispute mechanism"],
          definitionCitation: "Section 5.4 - Dispute Resolution"
        }
      },

      // Contract text with proper formal structure
      contractTexts: {
        originalText: {
          text: `SIMPLE AGREEMENT FOR FUTURE EQUITY

This Certifies that in exchange for the payment by Investor of the Purchase Amount, the Company issues to the Investor the right to certain shares of the Company's Capital Stock, subject to the terms described below.

1. DEFINITIONS AND INTERPRETATION

1.1 Qualified Financing
"Qualified Financing" means the Company's issuance and sale of equity securities in a bona fide transaction with the principal purpose of raising capital, with aggregate gross proceeds to the Company of at least One Million Dollars ($1,000,000), excluding any amounts raised under this and other SAFEs.

1.2 Change of Control
"Change of Control" means (i) a transaction or series of related transactions in which any person or group becomes the beneficial owner of more than 50% of the Company's voting securities, (ii) a sale, lease or other disposition of all or substantially all of the Company's assets, or (iii) a merger or consolidation of the Company with or into another entity.

1.3 Liquidity Event
"Liquidity Event" means (i) a Qualified Financing, (ii) a Change of Control, or (iii) an Initial Public Offering of the Company's common stock.

1.4 Capital Stock
"Capital Stock" means the capital stock of the Company, including common stock and preferred stock.

2. INVESTMENT TERMS

2.1 Investment Amount
Investor agrees to invest One Hundred Thousand Dollars ($100,000) (the "Purchase Amount") in exchange for the right to receive equity securities of the Company pursuant to the terms of this SAFE.

2.2 Valuation Cap
If this SAFE converts in connection with a Qualified Financing, the conversion price shall be calculated using a pre-money valuation cap of Five Million Dollars ($5,000,000) (the "Valuation Cap"), such that the effective price per share shall not exceed the Valuation Cap divided by the Company's fully-diluted capitalization.

2.3 Discount Rate
Alternatively, at Investor's election, the conversion price may be calculated by applying a twenty percent (20%) discount to the price per share paid by other investors in the Qualified Financing (the "Discount Rate"), provided that such discounted price shall not be less than the price determined by the Valuation Cap.

3. CONVERSION RIGHTS

3.1 Automatic Conversion
This SAFE will automatically convert into equity securities upon the occurrence of a Liquidity Event. The number of shares issued will be determined by dividing the Purchase Amount by either (i) the Valuation Cap conversion price, or (ii) the Discount Rate conversion price, whichever provides the greater number of shares to the Investor.

3.2 Optional Conversion
Prior to a Liquidity Event, the Investor may elect to convert this SAFE into common stock of the Company at a conversion price equal to the Valuation Cap divided by the Company's fully-diluted capitalization at the time of conversion, provided the Company consents to such conversion.

3.3 Pro Rata Participation Rights
Following conversion of this SAFE, Investor shall have the right to participate pro rata in future equity financings of the Company to maintain their percentage ownership, provided that such pro rata rights shall only apply if Investor's converted equity represents an investment of at least Fifty Thousand Dollars ($50,000) (the "Pro Rata Threshold") at the time of conversion.

4. COMPANY OBLIGATIONS

4.1 Information Rights
The Company shall deliver to Investor (i) unaudited quarterly financial statements within thirty (30) days after the end of each fiscal quarter, and (ii) audited annual financial statements within ninety (90) days after the end of each fiscal year, in each case prepared in accordance with generally accepted accounting principles.

4.2 Notice Requirements
The Company shall provide Investor with thirty (30) days prior written notice of (i) any proposed Liquidity Event, (ii) any material amendment to the Company's Certificate of Incorporation that would adversely affect the rights of SAFE holders, or (iii) any voluntary dissolution or winding up of the Company.

4.3 Dissolution
In the event of a voluntary or involuntary dissolution, liquidation or winding up of the Company prior to a Liquidity Event, the Investor shall be entitled to receive, prior and in preference to any distribution to holders of common stock, an amount equal to the Purchase Amount. If the Company's assets are insufficient to satisfy the full amounts payable to all SAFE holders, the Company's available assets will be distributed pro rata among SAFE holders in proportion to their respective Purchase Amounts.

5. GENERAL PROVISIONS

5.1 Assignment
The Investor may not transfer, assign or sell this SAFE or any of its rights hereunder without the prior written consent of the Company, except that Investor may transfer this SAFE to (i) any affiliate of Investor, (ii) any family member of Investor, or (iii) any trust or estate planning vehicle for the benefit of Investor or Investor's family members, provided that any such transferee agrees in writing to be bound by the terms of this SAFE.

5.2 Amendments
This SAFE and any provision hereof may only be amended, waived, discharged or terminated with the written consent of both the Company and the Investor. Any amendment or waiver effected in accordance with this Section shall be binding upon all parties hereto.

5.3 Governing Law
This SAFE shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to principles of conflicts of law. The parties hereby submit to the exclusive jurisdiction of the state and federal courts located in Delaware for the resolution of any disputes arising out of this SAFE.

5.4 Dispute Resolution
Any dispute, controversy or claim arising out of or relating to this SAFE, or the breach, termination or validity thereof, shall be settled by binding arbitration in Delaware in accordance with the Commercial Arbitration Rules of the American Arbitration Association. The arbitrator's award shall be final and binding, and judgment may be entered upon it in any court having jurisdiction thereof.

5.5 Entire Agreement
This SAFE constitutes the entire agreement between the parties with respect to the subject matter hereof and supersedes all prior agreements and understandings, both written and oral, between the parties with respect to the subject matter hereof.`
        },
        taggedText: {
          text: `SIMPLE AGREEMENT FOR FUTURE EQUITY

This Certifies that in exchange for the payment by Investor of the Purchase Amount, the Company issues to the Investor the right to certain shares of the Company's Capital Stock, subject to the terms described below.

1. DEFINITIONS AND INTERPRETATION

1.1 Qualified Financing
"<variable id="qualified_financing" type="discovered_term" level="1" instance="1">Qualified Financing</variable>" means the Company's issuance and sale of equity securities in a bona fide transaction with the principal purpose of raising capital, with aggregate gross proceeds to the Company of at least <variable id="qualified_financing" type="discovered_term" level="1" instance="2">One Million Dollars ($1,000,000)</variable>, excluding any amounts raised under this and other SAFEs.

1.2 Change of Control
"Change of Control" means (i) a transaction or series of related transactions in which any person or group becomes the beneficial owner of more than 50% of the Company's voting securities, (ii) a sale, lease or other disposition of all or substantially all of the Company's assets, or (iii) a merger or consolidation of the Company with or into another entity.

1.3 Liquidity Event
"<variable id="liquidity_event" type="discovered_term" level="1" instance="1">Liquidity Event</variable>" means (i) a <variable id="liquidity_event" type="discovered_term" level="1" instance="2">Qualified Financing</variable>, (ii) a <variable id="liquidity_event" type="discovered_term" level="1" instance="3">Change of Control</variable>, or (iii) an <variable id="liquidity_event" type="discovered_term" level="1" instance="4">Initial Public Offering</variable> of the Company's common stock.

1.4 Capital Stock
"Capital Stock" means the capital stock of the Company, including common stock and preferred stock.

2. INVESTMENT TERMS

2.1 Investment Amount
Investor agrees to invest <variable id="purchase_amount" type="discovered_term" level="1" instance="1">One Hundred Thousand Dollars ($100,000)</variable> (the "<variable id="purchase_amount" type="discovered_term" level="1" instance="2">Purchase Amount</variable>") in exchange for the right to receive equity securities of the Company pursuant to the terms of this SAFE.

2.2 Valuation Cap
If this SAFE converts in connection with a Qualified Financing, the conversion price shall be calculated using a pre-money <variable id="valuation_cap" type="discovered_term" level="1" instance="1">valuation cap of Five Million Dollars ($5,000,000)</variable> (the "<variable id="valuation_cap" type="discovered_term" level="1" instance="2">Valuation Cap</variable>"), such that the effective price per share shall not exceed the <variable id="valuation_cap" type="discovered_term" level="1" instance="3">Valuation Cap</variable> divided by the Company's fully-diluted capitalization.

2.3 Discount Rate
Alternatively, at Investor's election, the conversion price may be calculated by applying a <variable id="discount_rate" type="discovered_term" level="1" instance="1">twenty percent (20%) discount</variable> to the price per share paid by other investors in the Qualified Financing (the "<variable id="discount_rate" type="discovered_term" level="1" instance="2">Discount Rate</variable>"), provided that such discounted price shall not be less than the price determined by the Valuation Cap.

3. CONVERSION RIGHTS

3.1 Automatic Conversion
This SAFE will <variable id="conversion_trigger" type="discovered_term" level="1" instance="1">automatically convert into equity securities</variable> upon the occurrence of a <variable id="conversion_trigger" type="discovered_term" level="1" instance="2">Liquidity Event</variable>. The number of shares issued will be determined by dividing the Purchase Amount by either (i) the Valuation Cap conversion price, or (ii) the Discount Rate conversion price, whichever provides the greater number of shares to the Investor.

3.2 Optional Conversion
Prior to a Liquidity Event, the Investor may elect to convert this SAFE into common stock of the Company at a conversion price equal to the Valuation Cap divided by the Company's fully-diluted capitalization at the time of conversion, provided the Company consents to such conversion.

3.3 Pro Rata Participation Rights
Following conversion of this SAFE, Investor shall have the right to <variable id="pro_rata_rights" type="discovered_term" level="1" instance="1">participate pro rata in future equity financings</variable> of the Company to <variable id="pro_rata_rights" type="discovered_term" level="1" instance="2">maintain their percentage ownership</variable>, provided that such <variable id="pro_rata_rights" type="discovered_term" level="1" instance="3">pro rata rights</variable> shall only apply if Investor's converted equity represents an investment of at least <variable id="pro_rata_threshold" type="discovered_term" level="1" instance="1">Fifty Thousand Dollars ($50,000)</variable> (the "Pro Rata Threshold") at the time of conversion.

4. COMPANY OBLIGATIONS

4.1 Information Rights
The Company shall deliver to Investor (i) <variable id="information_rights" type="discovered_term" level="1" instance="1">unaudited quarterly financial statements within thirty (30) days</variable> after the end of each fiscal quarter, and (ii) <variable id="information_rights" type="discovered_term" level="1" instance="2">audited annual financial statements within ninety (90) days</variable> after the end of each fiscal year, in each case prepared in accordance with generally accepted accounting principles.

4.2 Notice Requirements
The Company shall provide Investor with <variable id="notice_period" type="discovered_term" level="1" instance="1">thirty (30) days prior written notice</variable> of (i) any proposed Liquidity Event, (ii) any material amendment to the Company's Certificate of Incorporation that would adversely affect the rights of SAFE holders, or (iii) any voluntary dissolution or winding up of the Company.

4.3 Dissolution
In the event of a voluntary or involuntary dissolution, liquidation or winding up of the Company prior to a Liquidity Event, the Investor shall be entitled to receive, prior and in preference to any distribution to holders of common stock, an amount equal to the <variable id="dissolution_priority" type="discovered_term" level="1" instance="1">Purchase Amount</variable>. If the Company's assets are insufficient to satisfy the full amounts payable to all SAFE holders, the Company's available assets will be distributed <variable id="dissolution_priority" type="discovered_term" level="1" instance="2">pro rata among SAFE holders</variable> in proportion to their respective Purchase Amounts.

5. GENERAL PROVISIONS

5.1 Assignment
The Investor may not transfer, assign or sell this SAFE or any of its rights hereunder without the <variable id="assignment_restrictions" type="discovered_term" level="1" instance="1">prior written consent of the Company</variable>, except that Investor may transfer this SAFE to (i) any <variable id="assignment_restrictions" type="discovered_term" level="1" instance="2">affiliate of Investor</variable>, (ii) any <variable id="assignment_restrictions" type="discovered_term" level="1" instance="3">family member of Investor</variable>, or (iii) any trust or estate planning vehicle for the benefit of Investor or Investor's family members, provided that any such transferee agrees in writing to be bound by the terms of this SAFE.

5.2 Amendments
This SAFE and any provision hereof may only be <variable id="amendment_requirements" type="discovered_term" level="1" instance="1">amended, waived, discharged or terminated with the written consent of both the Company and the Investor</variable>. Any amendment or waiver effected in accordance with this Section shall be binding upon all parties hereto.

5.3 Governing Law
This SAFE shall be <variable id="governing_law" type="discovered_term" level="1" instance="1">governed by and construed in accordance with the laws of the State of Delaware</variable>, without regard to principles of conflicts of law. The parties hereby submit to the exclusive jurisdiction of the state and federal courts located in <variable id="governing_law" type="discovered_term" level="1" instance="2">Delaware</variable> for the resolution of any disputes arising out of this SAFE.

5.4 Dispute Resolution
Any dispute, controversy or claim arising out of or relating to this SAFE, or the breach, termination or validity thereof, shall be settled by <variable id="dispute_resolution" type="discovered_term" level="1" instance="1">binding arbitration in Delaware</variable> in accordance with the Commercial Arbitration Rules of the <variable id="dispute_resolution" type="discovered_term" level="1" instance="2">American Arbitration Association</variable>. The arbitrator's award shall be final and binding, and judgment may be entered upon it in any court having jurisdiction thereof.

5.5 Entire Agreement
This SAFE constitutes the entire agreement between the parties with respect to the subject matter hereof and supersedes all prior agreements and understandings, both written and oral, between the parties with respect to the subject matter hereof.`
        }
      }
    },

    // EQ Mode Data
    eqData: {
      cards: {
        [EqCardKey.moneyYouReceive]: {
          id: EqCardKey.moneyYouReceive,
          title: "Money You Receive",
          type: EqCardType.A,
          cardUniqueData: {
            MONEY_RECEIVED: {
              majorNumber: "$100K",
              paidAfterList: [
                "Purchase Amount - $100K",
                "Valuation Cap - $5M",
                "Discount Rate - 20%"
              ]
            }
          },
          totalAdvance: "$100K",
          items: [
            {
              title: "Purchase Amount",
              value: "$100K"
            },
            {
              title: "Valuation Cap",
              value: "$5M"
            },
            {
              title: "Discount Rate",
              value: "20%"
            }
          ]
        },
        [EqCardKey.whatYouOwn]: {
          id: EqCardKey.whatYouOwn,
          title: "What You Own (and Don't)",
          type: EqCardType.A,
          cardUniqueData: {
            OWNERSHIP: {
              ownershipTerms: [
                {
                  title: "Equity Stake (at $5M cap)",
                  description: "2"
                }
              ]
            }
          },
          items: [
            {
              title: "Valuation Cap",
              value: "$5M"
            },
            {
              title: "Equity Stake",
              value: "2"
            }
          ]
        },
        [EqCardKey.howLongThisDealLasts]: {
          id: EqCardKey.howLongThisDealLasts,
          title: "How Long This Deal Lasts",
          type: EqCardType.B,
          cardUniqueData: {
            DURATION: {
              durationType: DurationType.fixed,
              durationText: "Converts upon Liquidity Event - no fixed term",
              durationDetails: [
                {
                  title: "Qualified Financing",
                  description: "$1M+ equity raise"
                },
                {
                  title: "Change of Control",
                  description: "Acquisition or merger"
                },
                {
                  title: "IPO",
                  description: "Public offering"
                }
              ]
            }
          },
          items: [
            {
              title: "Qualified Financing",
              value: "$1M minimum"
            },
            {
              title: "Conversion",
              value: "Upon liquidity event"
            }
          ]
        },
        [EqCardKey.whatYoureResponsibleFor]: {
          id: EqCardKey.whatYoureResponsibleFor,
          title: "What You're Responsible For",
          type: EqCardType.A,
          cardUniqueData: {
            RESPONSIBILITIES: {
              responsibilities: [
                {
                  title: "Financial Reporting",
                  description: "Quarterly & annual statements"
                },
                {
                  title: "Notice Requirements",
                  description: "30 days for liquidity events"
                },
                {
                  title: "Information Rights",
                  description: "Provide financials on request"
                }
              ]
            }
          },
          items: [
            {
              title: "Financial Reporting",
              value: "Quarterly & annual statements"
            },
            {
              title: "Notice Requirements",
              value: "30 days for liquidity events"
            },
            {
              title: "Information Rights",
              value: "Provide financials on request"
            }
          ]
        },
        [EqCardKey.risksCostsLegalStuff]: {
          id: EqCardKey.risksCostsLegalStuff,
          title: "Risks, Costs & Legal Stuff",
          type: EqCardType.A,
          cardUniqueData: {
            LEGAL: {
              risks: "No conversion if no qualified financing",
              costs: "Dissolution priority over common stock",
              legal: "Binding arbitration in Delaware"
            }
          },
          items: [
            {
              title: "Risks",
              value: "No conversion if no qualified financing"
            },
            {
              title: "Costs",
              value: "Dissolution priority over common stock"
            },
            {
              title: "Liabilities",
              value: "Binding arbitration in Delaware"
            }
          ]
        }
      }
    },

    // IQ Mode Data - Investor and Company Perspectives
    iqData: {
      iqModeData: {
        // Investor Perspective
        "Investor": {
          sections: {
            // Section 1: Earnings
            [IqModeSectionKey.earnings]: {
              id: IqModeSectionKey.earnings,
              sectionTitle: "Earnings",
              questions: [
                {
                  question: { text: "How much are you investing?" },
                  answer: { text: "You're investing $100,000 in exchange for the right to receive equity when the company raises a qualified financing round or has a liquidity event. This is the Purchase Amount that will convert into shares." }
                },
                {
                  question: { text: "What's your maximum valuation?" },
                  answer: { text: "Your valuation cap is $5 million, which protects you from dilution. This means no matter how high the company's valuation goes, your conversion price won't exceed what it would be at $5M. If the company raises at $10M, you still get shares as if it was valued at $5M." }
                },
                {
                  question: { text: "What discount do you get?" },
                  answer: { text: "You have a 20% discount on the price per share in the next funding round. Combined with the valuation cap, you get whichever gives you more shares—the cap price or the 20% discounted price." }
                }
              ]
            },

            // Section 2: Quality of Rights
            [IqModeSectionKey.qualityOfRights]: {
              id: IqModeSectionKey.qualityOfRights,
              sectionTitle: "Quality of Rights",
              questions: [
                {
                  question: { text: "What equity will you receive?" },
                  answer: { text: "Your equity percentage depends on when conversion happens. With the $5M cap, if the company is worth $5M at conversion, you'd get 2% ($100K/$5M). At a $10M valuation, the cap still gives you 2%. The exact shares are calculated by dividing your investment by either the cap price or discounted price, whichever is better for you." }
                },
                {
                  question: { text: "Can you invest more money later?" },
                  answer: { text: "Yes! You have pro rata rights to participate in future rounds to maintain your ownership percentage. However, this only applies if your converted equity represents at least $50,000 at the time of conversion." }
                },
                {
                  question: { text: "What happens to your SAFE if the company fails?" },
                  answer: { text: "In a dissolution, you have priority over common stockholders and get your $100,000 Purchase Amount back before they receive anything. However, if the company's assets are insufficient, you split what's available pro rata with other SAFE holders." }
                }
              ]
            },

            // Section 3: Usage & Obligations
            [IqModeSectionKey.usageObligations]: {
              id: IqModeSectionKey.usageObligations,
              sectionTitle: "Usage & Obligations",
              questions: [
                {
                  question: { text: "What information can you request?" },
                  answer: { text: "You're entitled to quarterly unaudited financial statements within 30 days of each quarter end and annual audited financials within 90 days of year end. This gives you visibility into the company's financial health." }
                },
                {
                  question: { text: "How much notice do you get for major decisions?" },
                  answer: { text: "The company must give you 30 days advance written notice before any liquidity event, material changes to their charter that affect SAFE holders, or any dissolution. This gives you time to understand and prepare for major changes." }
                },
                {
                  question: { text: "Can the company change the terms?" },
                  answer: { text: "No, not without your written consent. Any amendments to this SAFE require both you and the company to agree in writing. This protects your investment terms from being changed unilaterally." }
                }
              ]
            },

            // Section 4: Agreement Length
            [IqModeSectionKey.agreementLength]: {
              id: IqModeSectionKey.agreementLength,
              sectionTitle: "Agreement Length",
              questions: [
                {
                  question: { text: "How long does this SAFE last?" },
                  answer: { text: "There's no expiration date. This SAFE remains in effect until it converts to equity (at a qualified financing, change of control, or IPO) or until the company dissolves. It could be months or years depending on when the company raises its next funding round." }
                },
                {
                  question: { text: "Can you exit before conversion?" },
                  answer: { text: "No. Unlike debt with a maturity date, SAFEs don't have a predetermined endpoint where you get your money back. You're locked in until a conversion event happens or the company dissolves. There's no secondary market to sell your SAFE to someone else." }
                }
              ]
            },

            // Section 5: Liability & Safeguards
            [IqModeSectionKey.liabilitySafeguards]: {
              id: IqModeSectionKey.liabilitySafeguards,
              sectionTitle: "Liability & Safeguards",
              questions: [
                {
                  question: { text: "What if the company never raises funding?" },
                  answer: { text: "If there's no qualified financing ($1M+ raise) and no acquisition or IPO, your SAFE might never convert to equity. You'd have to wait for a dissolution to get your money back, or the company could stay private indefinitely with your SAFE outstanding." }
                },
                {
                  question: { text: "What's your maximum loss exposure?" },
                  answer: { text: "Your maximum loss is the full $100,000 investment if the company fails and has no assets to distribute. Unlike debt with security, SAFEs are unsecured and you only have priority over common stock in dissolution scenarios." }
                },
                {
                  question: { text: "How are disputes resolved?" },
                  answer: { text: "All disputes must be resolved through binding arbitration in Delaware under American Arbitration Association rules. This means you can't sue in court—arbitration is your only remedy, which can be faster but limits your options for appeal." }
                }
              ]
            }
          }
        },

        // Company Perspective
        "Company": {
          sections: {
            // Section 1: Earnings
            [IqModeSectionKey.earnings]: {
              id: IqModeSectionKey.earnings,
              sectionTitle: "Earnings",
              questions: [
                {
                  question: { text: "How much capital are you raising?" },
                  answer: { text: "You're receiving $100,000 in investment capital immediately without giving up equity now. This is cheaper and faster than a priced equity round, with no valuation negotiation required at this stage." }
                },
                {
                  question: { text: "What's the valuation cap impact?" },
                  answer: { text: "The $5M cap means the investor converts as if your company is worth $5M, regardless of your actual valuation at conversion. If you raise at $10M later, they still get shares at the $5M price—doubling their ownership compared to new investors." }
                },
                {
                  question: { text: "How does the discount affect your cap table?" },
                  answer: { text: "The 20% discount means the investor gets more shares than other investors in your next round. If new investors pay $1/share, the SAFE investor pays $0.80/share, getting 25% more shares for the same money. This compounds with the valuation cap." }
                }
              ]
            },

            // Section 2: Quality of Rights
            [IqModeSectionKey.qualityOfRights]: {
              id: IqModeSectionKey.qualityOfRights,
              sectionTitle: "Quality of Rights",
              questions: [
                {
                  question: { text: "How much equity will you give up?" },
                  answer: { text: "It depends on your future valuation. With the $5M cap, you'll give up at least 2% ($100K/$5M). If you raise at a lower valuation, they get more. If higher, the cap protects them at 2%. This dilution happens when you raise your Series A or have a liquidity event." }
                },
                {
                  question: { text: "What if you never raise a qualified round?" },
                  answer: { text: "The SAFE stays outstanding indefinitely until a qualifying event ($1M+ raise, acquisition, or IPO). You can't force conversion, and the investor keeps their rights. In a dissolution, you must pay them back before common stockholders get anything." }
                },
                {
                  question: { text: "Can the investor take control of the company?" },
                  answer: { text: "No. SAFEs only convert to equity upon specific events, and even then the 2% ownership from this SAFE won't give them control. They don't get board seats or voting rights until conversion, and their pro rata rights only let them maintain (not increase) their percentage." }
                }
              ]
            },

            // Section 3: Usage & Obligations
            [IqModeSectionKey.usageObligations]: {
              id: IqModeSectionKey.usageObligations,
              sectionTitle: "Usage & Obligations",
              questions: [
                {
                  question: { text: "What financial reporting must you provide?" },
                  answer: { text: "You must send quarterly unaudited financials within 30 days of quarter-end and annual audited statements within 90 days of year-end. This is ongoing until conversion or dissolution—audited statements can cost $5K-$25K+ annually." }
                },
                {
                  question: { text: "What notice obligations do you have?" },
                  answer: { text: "You must notify the investor 30 days before any liquidity event, charter amendments affecting SAFE holders, or dissolution. Missing these deadlines could give the investor grounds to claim you breached the agreement." }
                },
                {
                  question: { text: "Can you modify your charter freely?" },
                  answer: { text: "Not without considering SAFE holders. Any material amendments that adversely affect SAFE holder rights require 30 days notice. Plus, amendments to the SAFE itself need the investor's written consent—you can't unilaterally change terms." }
                }
              ]
            },

            // Section 4: Agreement Length
            [IqModeSectionKey.agreementLength]: {
              id: IqModeSectionKey.agreementLength,
              sectionTitle: "Agreement Length",
              questions: [
                {
                  question: { text: "How long are you obligated to this SAFE?" },
                  answer: { text: "Indefinitely until a conversion event or dissolution. You can't force the SAFE to expire—it stays on your cap table until you raise a qualified round ($1M+), get acquired, go public, or dissolve. If you stay profitable and never raise again, the SAFE could remain outstanding for years." }
                },
                {
                  question: { text: "Can you buy out the SAFE early?" },
                  answer: { text: "Not unless the agreement includes an optional redemption clause (which this one doesn't). Standard SAFEs don't give you the right to repurchase or terminate them early. The investor controls whether to accept an early exit, and they typically won't since they're betting on your upside." }
                }
              ]
            },

            // Section 5: Liability & Safeguards
            [IqModeSectionKey.liabilitySafeguards]: {
              id: IqModeSectionKey.liabilitySafeguards,
              sectionTitle: "Liability & Safeguards",
              questions: [
                {
                  question: { text: "What happens if you're acqui-hired?" },
                  answer: { text: "An acqui-hire typically counts as a Change of Control, triggering SAFE conversion. The investor gets equity, but in an acqui-hire the company often has little value. They might get nearly worthless shares, or you might owe them their $100K back if it's structured as a dissolution." }
                },
                {
                  question: { text: "What if you want to shut down?" },
                  answer: { text: "In a voluntary dissolution, the investor has priority over common stockholders to receive their $100K back. If you have multiple SAFEs and insufficient assets, all SAFE holders split what's available before founders get anything. This could leave you with nothing." }
                },
                {
                  question: { text: "Can you face legal disputes?" },
                  answer: { text: "Yes, but they go to arbitration in Delaware under AAA rules, not court. While arbitration is private and can be faster, it's also expensive (typically $10K+ in fees) and the arbitrator's decision is final with very limited appeal rights." }
                }
              ]
            }
          }
        }
      }
    }
  }
];

// ==================== DEMO CONTRACTS REGISTRY ====================
// Create a map for O(1) lookup by contract ID
const demoContractsMap = new Map<string, GetContractResponseContent>(
  demoContractsArray.map(contract => [contract.contractId, contract])
);

// ==================== EXPORTED DATA ====================
// Export the contracts array (for backwards compatibility)
export const musicIndustryContracts = demoContractsArray;

// Export all contracts for listing/iteration
export const demoContracts = demoContractsArray;

// ==================== GETTER FUNCTIONS ====================
/**
 * Get a demo contract by its ID
 * @param contractId - The contract ID to lookup
 * @returns The contract data or undefined if not found
 */
export const getDemoContractById = (contractId: string): GetContractResponseContent | undefined => {
  return demoContractsMap.get(contractId);
};

/**
 * Get all demo contracts
 * @returns Array of all demo contracts
 */
export const getAllDemoContracts = (): GetContractResponseContent[] => {
  return [...demoContractsArray];
};

/**
 * Get demo contracts by type
 * @param type - The contract type to filter by
 * @returns Array of contracts matching the type
 */
export const getDemoContractsByType = (type: string): GetContractResponseContent[] => {
  return demoContractsArray.filter(contract => contract.type === type);
};

/**
 * Get demo contract metadata (lightweight version without full content)
 * @param contractId - The contract ID
 * @returns Basic contract metadata
 */
export const getDemoContractMetadata = (contractId: string) => {
  const contract = getDemoContractById(contractId);
  if (!contract) return undefined;

  return {
    contractId: contract.contractId,
    name: contract.name,
    type: contract.type,
    ownerId: contract.ownerId,
  };
};

/**
 * Get all demo contract metadata
 * @returns Array of all contract metadata
 */
export const getAllDemoContractMetadata = () => {
  return demoContractsArray.map(contract => ({
    contractId: contract.contractId,
    name: contract.name,
    type: contract.type,
    ownerId: contract.ownerId,
  }));
};
